// BUILD-042 HR Administration: Employees, Org (departments), Checklists, Access, Audit.
// Every action here re-authorizes server-side (requirements AZ-10) — this UI only reflects what
// the server already permitted via GET /hr/me and per-route 403/404 responses.
//
// UAT local-usability cleanup (2026-10-05 decision) — integrates the working features into the
// approved compact workspace: the Employee detail view uses a single grouped section nav (one
// section visible at a time) instead of a long stack of forms; Personal & Contacts render as
// readable details with an explicit Edit/Save/Cancel cycle (PersonalContactsPanel); Audit shows
// human-readable attribution and local date/time (AuditPanel); checklist assignments
// distinguish due/assigned/completed and offer change history (ChecklistAssignmentsPanel);
// checklist responsibility options (task-routing labels) are now HR-admin-configurable
// (ChecklistResponsibilityOptionsPanel) — NEEDS DEV DEPLOY, see that panel's own note; plain-
// language "How this page works" help is added to Employees/Checklists/Access/Audit.
import { useEffect, useId, useRef, useState } from 'react';
import { Route, Routes, Navigate, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useAdminAttention } from './attentionStore';
import { badgeFor, employeeBadge, employeeChecklistKindCounts, employeeSectionCounts, parseAttentionSection, sectionBadge, type BadgeView } from './workspace/attentionModel';
import { AdminAttentionBanner } from './workspace/AttentionList';
import { AttentionBadge } from './workspace/AttentionBadge';
import {
  listHrAdminEmployees, listHrDepartments, createHrDepartment, renameHrDepartment, disableHrDepartment, enableHrDepartment, createHrRecord,
  listHrAccessGrants, applyHrAccessChanges,
  getHrAdminEmployee, createEmploymentChange,
  listHrChecklistTemplates, createHrChecklistTemplate, updateHrChecklistTemplateTasks,
  retireHrChecklistTemplate, restoreHrChecklistTemplate, listHrChecklistAssignmentsForEmployee, assignHrChecklist,
  listHrChecklistResponsibilityOptions, createHrChecklistResponsibilityOption,
  renameHrChecklistResponsibilityOption, retireHrChecklistResponsibilityOption,
  createFictionalFixtureEmployeeForUser, invalidateHrAdminEmployeesCache, listHrDocumentAssignmentsForEmployee,
  listHrDocumentHistoryReasons, createHrDocumentHistoryReason, editHrDocumentHistoryReason,
  retireHrDocumentHistoryReason, reactivateHrDocumentHistoryReason,
  type HrAccessChange, type HrAccessGrantRow, type HrDepartment,
  type HrAdminEmployeeListEntry, type HrChecklistTemplate, type HrChecklistAssignment,
  type HrChecklistResponsibilityOption, type HrDocumentAssignment, type HrDocumentHistoryReason,
} from '../../api/client';
import { EmployeePicker } from './EmployeePicker';
import { splitTermHistory, type TermLike } from './termHistory';
import { resolveDisplayName } from './hrFormat';
import { CollapsibleHelp } from './CollapsibleHelp';
import { CREATE_FICTIONAL_PERSON_AVAILABLE } from './hrFeatures';
import { SelfSetupCard } from './SelfSetupCard';
import { useHrAccess } from './HrAccessContext';
import { PersonalContactsSelfSection } from './workspace/PersonalContactsSelfSection';
import { ChecklistAssignmentsPanel } from './ChecklistAssignmentsPanel';
import { AssignChecklistForm } from './AssignChecklistForm';
import { AdminChecklistsSection as ChecklistAreaForEmployee } from './checklist/AdminChecklistsSection';
import { AuditPanel } from './AuditPanel';
import { CorrectionRequestsPanel } from './CorrectionRequestsPanel';
import { AdminDocumentsSection } from './workspace/AdminDocumentsSection';
import { DocumentTemplatesPanel } from './DocumentTemplatesPanel';
import { BenefitsManagerWorkspace } from './BenefitsManagerWorkspace';
import { OnboardingOffboardingWorkspace, ManageChecklistTemplatesLink, WorkspaceIcon, type OnboardingOffboardingTab } from './workspace/OffboardingSection';
import { formatLocalDate } from './hrFormat';
import { AdminBenefitsEligibilitySection } from './workspace/AdminBenefitsEligibilitySection';
import { WorkspaceNav } from './workspace/WorkspaceNav';
import { EmployeeCard } from './workspace/EmployeeCard';
import { Breadcrumbs } from './workspace/Breadcrumbs';
import { OverviewSection } from './workspace/OverviewSection';
import { PlannedSection } from './workspace/PlannedSection';
import { PropertySection } from './workspace/PropertySection';
import { usePropertySchedule } from './workspace/usePropertySchedule';
import { actionErrorMessage, classifyLoadError, loadErrorKey, needsPermissionMessage, PERMISSION_LABEL, useSurfaceFlag, type LoadView } from './workspace/loadState';
import { DeniedNotice, LoadViewNotice, PermissionHint, TargetUnavailableNotice } from './workspace/LoadNotices';
import { SectionHeading, PlannedBadge, CardTitle } from './workspace/SectionHeading';
import { isBuilt, isNonRestricted, SECTION_NAV_GROUPS, subtitleFor, type SectionKey } from './workspace/sectionGroups';
import { AdminNav } from './workspace/AdminNav';
import { HrWorkspaceFrame } from './workspace/HrWorkspaceFrame';
import { CompanySetupPanel } from './workspace/CompanySetupPanel';
import { EmployeesPanel } from './EmployeesWorkspace';
import { AdminPlannedPanel, type AdminPlannedContent } from './workspace/AdminPlannedPanel';
import {
  capabilityLabel, scopeLabel, EDITABLE_PERMISSIONS, editablePermission, editableScopeLabel, editableShortLabel, accessRefusalMessage, type AccessScope,
} from './workspace/accessLabels';
import { AboutHrAccess, AccessSummary, PermissionRow } from './workspace/AccessCompact';
import { ADMIN_NAV_GROUPS, adminLabelFor, adminSubtitleFor, isCompanySetupChild, railKeyFor, type AdminNavKey } from './workspace/adminNavGroups';
import { Avatar } from './workspace/Avatar';
import { PendingWorkPanel } from './PendingWorkPanel';
import { Link as SetupLink } from 'react-router-dom';
import { SetupWizard } from './setup/SetupWizard';
import { AdminEmployeeOverview } from './setup/AdminEmployeeOverview';
import { useSetupGate } from './setup/useSetupGate';
import { setupPath, shouldRedirectToSetup, hasExplicitDeepLink } from './setup/setupModel';

interface Props {
  isPrimaryOwner: boolean;
  /** BUILD-042 My HR self-service & change-request decision (2026-10-06): needed by
   *  CorrectionRequestsPanel to enforce no-self-approval in its local mock — the viewer's own
   *  requests must never show an enabled Approve button. */
  viewerEmployeeId: string;
}

// 'documents' is deliberately absent here — it renders the real DocumentTemplatesPanel (Form
// Library), not AdminPlannedPanel, since the October 8 22:34 Phoenix addendum made it a real,
// built destination.
// 'benefits' is deliberately absent here — it renders the real BenefitPlansPanel (simple
// benefits, migration 207), not AdminPlannedPanel, since this increment made it a built
// destination (matching how 'documents' became the real Form Library).
const PLANNED_CONTENT: Record<'training' | 'timeoff', AdminPlannedContent> = {
  training: {
    title: 'Training catalog', subtitle: 'Reusable training resources and requirements for your team.',
    tiles: ['New-hire orientation', 'Equipment training', 'License requirements'],
    settingsFields: ['Video, document or external course link', 'Required audience and renewal interval', 'Completion and verification rules'],
    note: 'Videos and resources',
  },
  timeoff: {
    title: 'Time-off policies', subtitle: 'Configure HR-side entitlement terms and request rules.',
    tiles: ['Vacation', 'Sick time', 'General leave'],
    settingsFields: ['Policy version and effective dates', 'Employee assignment and agreed entitlement', 'Approval routing and payroll reconciliation'],
    note: 'QuickBooks integration planned',
  },
};

/** Resolves the current AdminNavKey from the actual route, so the breadcrumb/heading/nav
 *  selected-state stay correct regardless of which path got the caller here (including the
 *  employee-detail route, which maps back to 'employees'). */
function useActiveAdminKey(): AdminNavKey {
  const location = useLocation();
  const path = location.pathname.replace(/\/+$/, '') || '/';
  if (path.startsWith('/hr/admin/employees')) return 'employees';
  const match = ADMIN_NAV_GROUPS.flatMap((g) => g.items).find((i) => i.path === path);
  return match?.key ?? 'employees';
}

// Oct 24 (Owner decision, "Overall HR Administration composition"): the page is a shared workspace frame - a compact secondary rail pinned beside the
// global HQ navigation and a left-aligned, readable content column - instead of a centred two-column block. The repeated attention banner above every
// page is gone: open work is the third tab on the Employees page (EmployeesWorkspace.tsx), and /hr/admin/pending redirects there.
export function HrAdminTab({ isPrimaryOwner, viewerEmployeeId }: Props) {
  const activeKey = useActiveAdminKey();
  const location = useLocation();
  const isEmployeeDetail = location.pathname.startsWith('/hr/admin/employees/') && location.pathname !== '/hr/admin/employees';

  // The Employee-detail route renders its OWN full breadcrumbs/EmployeeCard/WorkspaceNav shell
  // (EmployeeDetailPanel, unchanged from the prior pass — the full 16-destination unified
  // employee workspace, which this decision explicitly says must NOT be replaced by the
  // reference's simplified 4-tab drilldown). It does not share this admin-nav shell at all.
  if (isEmployeeDetail) {
    return (
      <Routes>
        <Route path="/employees/:employeeId" element={<EmployeeDetailPanel viewerEmployeeId={viewerEmployeeId} />} />
        {/* Oct 24: guided initial HR setup for ONE person (its own rail replaces the ordinary employee navigation). */}
        <Route path="/employees/:employeeId/setup" element={<SetupWizard viewerEmployeeId={viewerEmployeeId} />} />
      </Routes>
    );
  }

  const railKey = railKeyFor(activeKey);
  const sectionLabel = adminLabelFor(activeKey);
  const isLanding = activeKey === 'employees' || activeKey === 'companySetup';
  const crumbs = isCompanySetupChild(activeKey)
    ? [{ label: 'HR Administration', to: '/hr/admin' }, { label: 'Company setup', to: '/hr/admin/company-setup' }, { label: sectionLabel }]
    : [{ label: 'HR Administration', to: '/hr/admin' }, { label: sectionLabel }];

  return (
    <HrWorkspaceFrame railLabel="HR Administration menu" currentLabel={adminLabelFor(railKey)} rail={<AdminNav active={railKey} />} testId="hr-admin-frame" wide={activeKey === 'documents'}>
      {!isLanding && <Breadcrumbs items={crumbs} />}
      <Routes>
        <Route path="/" element={<EmployeesPanel viewerEmployeeId={viewerEmployeeId} />} />
        {/* The separate Pending work page became the Open work tab (Oct 24). The old route keeps working. */}
        <Route path="/pending" element={<Navigate to="/hr/admin?tab=open" replace />} />
        <Route path="/company-setup" element={<CompanySetupPanel />} />
        <Route path="/org" element={<OrgPanel />} />
        <Route path="/checklists" element={<ChecklistTemplatesPanel />} />
        <Route path="/labels" element={<ResponsibilityLabelsPage />} />
        <Route path="/history-reasons" element={<DocumentHistoryReasonsPage />} />
        <Route path="/benefits" element={<BenefitsManagerWorkspace />} />
        <Route path="/training" element={<AdminPlannedPanel content={PLANNED_CONTENT.training} />} />
        <Route path="/documents" element={<DocumentTemplatesPanel />} />
        <Route path="/timeoff" element={<AdminPlannedPanel content={PLANNED_CONTENT.timeoff} />} />
        <Route path="/access" element={<AccessPanel isPrimaryOwner={isPrimaryOwner} viewerEmployeeId={viewerEmployeeId} />} />
        <Route path="/audit" element={<AuditPanel />} />
        <Route path="/correction-requests" element={<CorrectionRequestsPanel viewerEmployeeId={viewerEmployeeId} highlightRequestId={new URLSearchParams(location.search).get('request') ?? undefined} />} />
        <Route path="*" element={<Navigate to="/hr/admin" replace />} />
      </Routes>
    </HrWorkspaceFrame>
  );
}

/** Oct 20: the existing no-self-finalization rule, in plain words. Nobody may record or finalize an employment change on their OWN record, the Primary Owner included
 *  (it keeps an HR administrator from approving changes to their own title, department or reporting line). Another HR administrator records it. */
const OWN_RECORD_CHANGE_MESSAGE = 'You can’t record changes to your own employment record, including your department. This is a built-in safeguard that applies to everyone. Another HR administrator can record the change for you (you can give someone the “HR management” permission under Access). If something on your record is wrong, use “Request a correction” in My HR.';

interface AdminTerm extends TermLike {
  title: string | null;
  departmentId: string | null;
  managerEmployeeId: string | null;
  classification: string | null;
  reason?: string | null;
}

function describeTerm(t: { title: string | null; departmentId?: string | null; managerEmployeeId: string | null; reason?: string | null }, managerNameById?: Record<string, string | null>, departmentName?: string | null): string {
  const parts: string[] = [];
  parts.push(t.title ?? '(no title)');
  if (t.managerEmployeeId) {
    const name = managerNameById?.[t.managerEmployeeId];
    parts.push(`manager: ${name ?? t.managerEmployeeId}`);
  }
  // Oct 20: the department by NAME (a disabled one included), never the stored id. Left out until the names are loaded.
  if (t.departmentId && departmentName) parts.push(`dept: ${departmentName}`);
  if (t.reason) parts.push(`reason: ${t.reason}`);
  return parts.join(' · ');
}

// Unified employee workspace (2026-10-06/07, Owner-approved layout). Authority:
// docs/superpowers/decisions/2026-10-06-build-042-approved-unified-employee-layout.md. The
// Employee detail view now uses the SAME compact employee card + grouped four-category nav +
// breadcrumbs shell as My HR (workspace/*.tsx), instead of its own smaller 5-item nav — so an
// HR Administrator sees the full approved 16-destination map for the Employee they are viewing,
// with real functionality (Overview, Employment & Pay, Personal & Contacts, Checklists,
// History & Corrections) wired in and every other destination an honest Planned placeholder.
// Breadcrumbs reflect administration entry context (HR -> Employees -> employee name ->
// section) and "Employees" stays a real clickable route back to the roster per the decision's
// explicit breadcrumb requirement.
function EmployeeDetailPanel({ viewerEmployeeId }: { viewerEmployeeId: string }) {
  const { employeeId } = useParams<{ employeeId: string }>();
  const effectiveDateId = useId();
  const [record, setRecord] = useState<{ employeeId: string; name: string | null; managerEmployeeId: string | null; managerName: string | null; period: any; termHistory: AdminTerm[] } | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Oct 17: an attention link lands on this employee's Documents/Checklists section, with the right document row open.
  const [params] = useSearchParams();
  const linkedSection = parseAttentionSection(params.get('section'));
  const focusItem = params.get('item');
  // Cycle 12: guided setup links here ("View tasks") with router state { returnTo: <setup step> }; only a path inside THIS employee's setup is honoured.
  const routerReturnTo = (useLocation().state as { returnTo?: unknown } | null)?.returnTo;
  const setupReturnTo = typeof routerReturnTo === 'string' && employeeId && routerReturnTo.startsWith(`/hr/admin/employees/${encodeURIComponent(employeeId)}/setup`) ? routerReturnTo : null;
  const [activeSection, setActiveSection] = useState<SectionKey>(linkedSection ?? 'overview');
  useEffect(() => { if (linkedSection) setActiveSection(linkedSection); }, [linkedSection, focusItem]);
  const [lifecycleVersion, setLifecycleVersion] = useState(0);
  const adminAttention = useAdminAttention();
  // Oct 24: where this person's guided initial HR setup stands, read from the server (a legacy record has none and is never sent to setup).
  const setupGate = useSetupGate(employeeId);
  const employeeCounts = employeeSectionCounts(adminAttention, employeeId ?? '');
  const sectionBadges: Partial<Record<SectionKey, BadgeView>> = {
    documents: sectionBadge(employeeCounts.documents, employeeCounts.known, adminAttention.status === 'error', 'Documents & Signatures'),
    checklists: sectionBadge(employeeCounts.checklists, employeeCounts.known, adminAttention.status === 'error', 'Onboarding & Offboarding'),
  };
  // Cycle 13: the Onboarding and Offboarding rail items each count ONLY their own open checklist tasks (same definition as the combined number, split by kind).
  const kindCounts = employeeChecklistKindCounts(adminAttention, employeeId ?? '');
  const checklistKindBadges: Partial<Record<'onboarding' | 'offboarding', BadgeView>> = {
    onboarding: sectionBadge(kindCounts.onboarding, kindCounts.known, adminAttention.status === 'error', 'Onboarding'),
    offboarding: sectionBadge(kindCounts.offboarding, kindCounts.known, adminAttention.status === 'error', 'Offboarding'),
  };
  // One schedule fetch feeds BOTH the Overview "Property issued" tile and the Equipment & Property list (server enforces HR authority).
  const { load: propertyLoad, reload: reloadProperty } = usePropertySchedule('admin', employeeId);
  useEffect(() => { reloadProperty(); }, [activeSection, reloadProperty]);
  const [effectiveDate, setEffectiveDate] = useState('');
  const [title, setTitle] = useState('');
  const [managerEmployeeId, setManagerEmployeeId] = useState('');
  const [reason, setReason] = useState('');
  const [status, setStatus] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [names, setNames] = useState<Record<string, string | null>>({});
  // Oct 20: every department (active AND disabled) so a department is always shown by name, and the one the person holds today stays readable even when disabled.
  const [allDepartments, setAllDepartments] = useState<HrDepartment[]>([]);
  // '' = keep the current department (the department is then NOT sent, so the server carries it forward untouched).
  const [departmentChoice, setDepartmentChoice] = useState('');
  const departmentSelectId = useId();
  useEffect(() => {
    listHrDepartments({ includeDisabled: true }).then((d) => setAllDepartments(d.departments)).catch(() => setAllDepartments([]));
  }, []);
  const departmentNameOf = (id: string | null | undefined): string | null => (id ? allDepartments.find((d) => d.departmentId === id)?.name ?? null : null);

  const refresh = () => {
    if (!employeeId) return;
    getHrAdminEmployee(employeeId).then(setRecord).catch((e: any) => setError(e.message || 'Could not load this Employee.'));
  };
  useEffect(() => { refresh(); }, [employeeId]);

  // Reused for every name resolution on this page (manager history, personal-detail/checklist
  // attribution) — one fetch, not a separate directory call per section.
  useEffect(() => {
    listHrAdminEmployees()
      .then((d) => setNames(Object.fromEntries(d.employees.map((e) => [e.employeeId, e.name]))))
      .catch(() => setNames({}));
  }, []);

  async function handleAddChange() {
    if (!employeeId || !effectiveDate || submitting) return;
    setSubmitting(true);
    try {
      await createEmploymentChange(employeeId, {
        effectiveDate,
        title: title || undefined,
        departmentId: departmentChoice || undefined,
        managerEmployeeId: managerEmployeeId || undefined,
        reason: reason || undefined,
      });
      setStatus('Employment change recorded.');
      setEffectiveDate(''); setTitle(''); setDepartmentChoice(''); setManagerEmployeeId(''); setReason('');
      refresh();
    } catch (e: any) {
      // The server's no-self-finalization rule is explained in plain words, never shown as its raw code. Nothing about the rule changes.
      if (e?.status === 403 && /no-self-finalization/.test(String(e?.message || ''))) setStatus(OWN_RECORD_CHANGE_MESSAGE);
      else setStatus(e.message || 'Failed to record employment change.');
    } finally {
      setSubmitting(false);
    }
  }

  // Oct 24: an unfinished NEW record opens its guided setup instead of the normal page (replace: no redirect loop, Back still works). An explicit ?section= / ?item=
  // deep link opens the normal page with a slim banner instead. Wait for the answer so the normal page does not flash first.
  const setupInProgress = setupGate.view?.progress?.status === 'in_progress';
  if (employeeId && setupGate.state === 'ready' && shouldRedirectToSetup(setupGate.view?.progress?.status, `?${params.toString()}`)) {
    return <Navigate to={setupPath(employeeId)} replace />;
  }
  if (setupGate.state === 'loading' && !hasExplicitDeepLink(`?${params.toString()}`)) return <div className="text-gray-500" role="status" data-testid="setup-gate-loading">Loading…</div>;

  if (error) return <div className="text-red-700">{error}</div>;
  if (!record) return <div className="text-gray-500">Loading…</div>;

  const { current, upcoming, history } = splitTermHistory(record.termHistory);
  const employeeLabel = record.name ?? record.employeeId;
  const isOwnRecord = !!employeeId && employeeId === viewerEmployeeId;
  const currentDepartmentName = departmentNameOf(current?.departmentId);
  const currentDepartmentDisabled = !!current?.departmentId && allDepartments.find((d) => d.departmentId === current.departmentId)?.disabled === true;
  const sectionLabel = SECTION_NAV_GROUPS.flatMap((g) => g.items).find((i) => i.key === activeSection)?.label ?? activeSection;

  return (
    <div>
      <Breadcrumbs
        items={[
          { label: 'HR', to: '/hr/admin' },
          { label: 'Employees', to: '/hr/admin' },
          { label: employeeLabel, onClick: () => setActiveSection('overview') },
          { label: sectionLabel },
        ]}
      />

      <HrWorkspaceFrame railLabel="Employee menu" currentLabel={sectionLabel} testId="hr-employee-frame" stickyRail rail={
          // Owner decision 2026-10-25 (preview/release parity): a SEPARATE employee identity card
          // (the same EmployeeCard used by My HR, without the photo-change affordance) sits above a
          // single FLAT rail — no faint uppercase grouping headings. The identity no longer lives
          // inside the rail. Every destination, child and ?tab= deep link is preserved.
          <>
            <EmployeeCard
              info={{
                name: employeeLabel,
                title: current?.title ?? null,
                departmentId: current?.departmentId ?? null,
                employmentStatus: record.period?.employmentStatus ?? null,
                hireDate: record.period?.hireDate ?? null,
                allowPhotoChange: false,
              }}
            />
            <WorkspaceNav
              active={activeSection}
              onSelect={setActiveSection}
              visible={isNonRestricted}
              context="admin"
              badges={sectionBadges}
              checklistBadges={checklistKindBadges}
            />
          </>
        }>
        <div className="space-y-4 min-w-0">
          {/* Oct 24: a deep link into an unfinished new record opens the normal page, with a slim way back to setup. */}
          {setupInProgress && employeeId && (
            <div role="status" className="rounded-md border border-amber-300 bg-amber-50 px-4 py-2 text-sm text-amber-900 flex flex-wrap items-center justify-between gap-2" data-testid="setup-unfinished-banner">
              <span>Initial HR setup isn’t finished.</span>
              {setupReturnTo
                ? <SetupLink to={setupReturnTo} className="font-medium text-[#176A9B] hover:underline" data-testid="back-to-setup">← Back to setup</SetupLink>
                : <SetupLink to={setupPath(employeeId)} className="font-medium text-[#176A9B] hover:underline" data-testid="setup-banner-continue">Continue setup →</SetupLink>}
            </div>
          )}
          {/* Oct 24: the HR management overview carries its own name, "HR management" line and badges, so the generic section heading is not repeated above it. */}
          {activeSection !== 'overview' && (
            <SectionHeading
              title={sectionLabel}
              subtitle={subtitleFor(activeSection)}
              badge={!isBuilt(activeSection, 'admin') ? <PlannedBadge /> : undefined}
            />
          )}
          {activeSection === 'overview' && employeeId && (
            <AdminEmployeeOverview
              employeeId={employeeId}
              viewerEmployeeId={viewerEmployeeId}
              record={record}
              names={names}
              departmentName={departmentNameOf}
              attention={adminAttention}
              gate={setupGate}
              onNavigate={setActiveSection}
              onChanged={refresh}
            />
          )}

          {activeSection === 'employment' && (
            <>
              {upcoming.length > 0 && (
                <div className="bg-white rounded-lg border border-amber-200 p-5">
                  <h2 className="text-sm font-semibold text-amber-700 uppercase tracking-wide mb-3">Upcoming Change{upcoming.length > 1 ? 's' : ''}</h2>
                  {upcoming.map((t, i) => <div key={`${t.effectiveDate}-${i}`} className="text-sm py-1">{t.effectiveDate}: {describeTerm(t, names, departmentNameOf(t.departmentId))}</div>)}
                </div>
              )}
              <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-5">
                <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">Record Employment Change</h2>
                {/* Oct 20: on your OWN record the server refuses (no-self-finalization). Say so up front, in plain words, instead of letting a raw code appear after a click. */}
                {isOwnRecord && (
                  <p className="mb-3 rounded-md bg-[#EDF5FC] px-3 py-2 text-xs text-gray-700" data-testid="own-record-change-note">{OWN_RECORD_CHANGE_MESSAGE}</p>
                )}
                <fieldset disabled={isOwnRecord} className="min-w-0 border-0 p-0 m-0">
                  <div className="grid grid-cols-2 gap-2">
                    <div className="flex flex-col gap-1">
                      <label htmlFor={effectiveDateId} className="text-xs text-gray-500">Effective date</label>
                      <input id={effectiveDateId} type="date" className="border border-gray-300 rounded px-3 py-1.5 text-sm" value={effectiveDate} onChange={(e) => setEffectiveDate(e.target.value)} />
                    </div>
                    <div className="flex flex-col gap-1">
                      <label htmlFor={`${departmentSelectId}-title`} className="text-xs text-gray-500">Title</label>
                      <input id={`${departmentSelectId}-title`} className="border border-gray-300 rounded px-3 py-1.5 text-sm" placeholder="Title" value={title} onChange={(e) => setTitle(e.target.value)} />
                    </div>
                    {/* Oct 20: a Department choice. Only ACTIVE departments are offered for a new selection; leaving it on "Keep current" sends nothing, so the current department
                        (disabled or not) is carried forward untouched. */}
                    <div className="flex flex-col gap-1">
                      <label htmlFor={departmentSelectId} className="text-xs text-gray-500">Department</label>
                      <select id={departmentSelectId} className="border border-gray-300 rounded px-3 py-1.5 text-sm bg-white" value={departmentChoice} onChange={(e) => setDepartmentChoice(e.target.value)} data-testid="employment-change-department">
                        <option value="">{currentDepartmentName ? `Keep current department (${currentDepartmentName}${currentDepartmentDisabled ? ', disabled' : ''})` : 'Keep current department'}</option>
                        {allDepartments.filter((d) => !d.disabled && d.departmentId !== current?.departmentId).map((d) => <option key={d.departmentId} value={d.departmentId}>{d.name}</option>)}
                      </select>
                    </div>
                    <div className="flex flex-col gap-1">
                      <span className="text-xs text-gray-500">New manager</span>
                      <EmployeePicker value={managerEmployeeId} onChange={setManagerEmployeeId} placeholder="New manager (optional)" />
                    </div>
                    <input className="col-span-2 border border-gray-300 rounded px-3 py-1.5 text-sm" placeholder="Reason" aria-label="Reason" value={reason} onChange={(e) => setReason(e.target.value)} />
                  </div>
                  <button onClick={handleAddChange} disabled={submitting || !effectiveDate} className="mt-3 bg-[#4AABDE] text-white text-sm px-4 py-1.5 rounded disabled:opacity-50 disabled:cursor-not-allowed">
                    {submitting ? 'Recording…' : 'Record Change'}
                  </button>
                </fieldset>
                {status && <div className="text-sm text-gray-600 mt-2" data-testid="employment-change-status">{status}</div>}
              </div>
              <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-5">
                <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">Term History</h2>
                {history.length === 0 ? (
                  <div className="text-sm text-gray-400">No prior terms.</div>
                ) : (
                  history.map((t, i) => <div key={`${t.effectiveDate}-${i}`} className="text-sm py-1 border-t border-gray-100">{t.effectiveDate}: {describeTerm(t, names, departmentNameOf(t.departmentId))}</div>)
                )}
              </div>
            </>
          )}

          {activeSection === 'contacts' && employeeId && (
            <PersonalContactsSelfSection
              employeeId={employeeId}
              employeeName={employeeLabel}
              legalName={record.name ?? employeeId}
              preferredName={null}
              isSelf={false}
              nameMap={names}
            />
          )}

          {activeSection === 'checklists' && employeeId && (
            <>
              {/* Oct 23 approved layout: local Onboarding / Offboarding tabs inside this section. The lifecycle cards (schedule, cancel, Employee list, account status) show only to
                  someone who holds hr.lifecycle.manage (the server answers 404 otherwise); the type-specific checklist cards use the existing assignment and completion behavior.
                  A change on the Offboarding tab (for example scheduling a departure assigns an exit checklist) remounts the checklist cards so they show the result. */}
              <OnboardingOffboardingWorkspace
                employeeId={employeeId}
                employeeName={employeeLabel}
                initialTab={params.get('tab') === 'offboarding' ? 'offboarding' : params.get('tab') === 'onboarding' ? 'onboarding' : undefined}
                focusTaskId={linkedSection === 'checklists' ? focusItem : null}
                onChanged={() => setLifecycleVersion((v) => v + 1)}
                renderChecklist={(kind) => (
                  <AdminChecklistsSection key={`tasks-${kind}-${focusItem ?? ''}-${lifecycleVersion}`} kind={kind} employeeId={employeeId} employeeName={employeeLabel} nameMap={names} focusTaskId={linkedSection === 'checklists' ? focusItem : null} defaultHireDate={record.period?.hireDate ?? null} />
                )}
              />
            </>
          )}

          {activeSection === 'history' && employeeId && (
            <CorrectionRequestsPanel viewerEmployeeId={viewerEmployeeId} filterEmployeeId={employeeId} />
          )}

          {activeSection === 'documents' && employeeId && (
            <AdminDocumentsSection key={`docs-${focusItem ?? ''}`} employeeId={employeeId} focusAssignmentId={linkedSection === 'documents' ? focusItem : null} />
          )}
          {activeSection === 'property' && employeeId && (
            <PropertySection load={propertyLoad} mode="admin" employeeId={employeeId} onReload={reloadProperty} />
          )}

          {activeSection === 'benefits' && employeeId && (
            <AdminBenefitsEligibilitySection employeeId={employeeId} employeeName={employeeLabel} hireDate={record.period?.hireDate ?? null} />
          )}

          {(activeSection === 'time' || activeSection === 'training'
            || activeSection === 'reviews'
            || activeSection === 'custom') && (
            <PlannedSection sectionKey={activeSection} employeeId={employeeId} />
          )}
        </div>
      </HrWorkspaceFrame>
    </div>
  );
}

// Cycle 13: the Onboarding / Offboarding checklist area (one per process kind) lives in checklist/AdminChecklistsSection.tsx, which shares its layout with the guided-setup step.
function AdminChecklistsSection(props: { employeeId: string; employeeName: string; nameMap: Record<string, string | null>; focusTaskId?: string | null; kind: OnboardingOffboardingTab; defaultHireDate?: string | null }) {
  return <ChecklistAreaForEmployee {...props} />;
}

/** Oct 20: what Add / Rename department shows when the server says no. A duplicate or a rule the server explains is shown as it explains it; anything
 *  unexpected (a server fault) is never passed through as raw database text. The entered name is always kept by the caller. */
function departmentErrorMessage(e: any, action: 'add' | 'rename' | 'disable' | 'enable', name: string): string {
  const status = e?.status;
  // A re-enable that collides with an active department's name carries its own plain explanation from the server (and merged/renamed nothing).
  if (status === 409 || status === 400) return String(e?.message || (status === 409 ? `A department named “${name}” already exists.` : 'Check the department name and try again.'));
  if (status === 403 || status === 404) return 'You don’t have permission to change departments.';
  const did = action === 'add' ? 'added' : action === 'rename' ? 'renamed' : action === 'disable' ? 'disabled' : 're-enabled';
  return `“${name}” couldn’t be ${did} right now. Nothing was changed${action === 'add' || action === 'rename' ? ' and your entry is still here' : ''}. Please try again.`;
}

function OrgPanel() {
  const navigate = useNavigate();
  const [departments, setDepartments] = useState<HrDepartment[] | null>(null);
  // Oct 20: disabled departments are hidden by default; this checkbox reveals them (with a Disabled badge and Re-enable).
  const [showDisabled, setShowDisabled] = useState(false);
  // Inline Disable confirmation (which department is being confirmed), a brief success notice, and a per-row error (a refused Re-enable explains itself here).
  const [confirmDisableId, setConfirmDisableId] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [rowError, setRowError] = useState<{ id: string; text: string } | null>(null);
  const [name, setName] = useState('');
  const [createError, setCreateError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // Inline rename: which department is being edited, the draft name, and any error for that row.
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  const [editError, setEditError] = useState<string | null>(null);
  const [employees, setEmployees] = useState<HrAdminEmployeeListEntry[]>([]);
  // A failed department list-load must NOT look like a genuine empty list (Oct 19 appendix): `departments === null`
  // means "not loaded yet"; `loadError` means the read failed; only a successful read of zero rows is a true empty list.
  const [loadError, setLoadError] = useState<string | null>(null);

  const refresh = () => {
    setLoadError(null);
    // Always load every department with its disabled flag; the checkbox decides what is drawn. (An older server that ignores the option returns the active ones only.)
    return listHrDepartments({ includeDisabled: true })
      .then((d) => setDepartments(d.departments))
      .catch((e: any) => { setDepartments(null); setLoadError(e?.message || 'Could not load departments. Please try again.'); });
  };
  useEffect(() => {
    refresh();
    listHrAdminEmployees().then((d) => setEmployees(d.employees.filter((e) => e.hasHrRecord))).catch(() => setEmployees([]));
  }, []);

  async function handleCreate() {
    const value = name.trim();
    setCreateError(null);
    if (!value) { setCreateError('Enter a department name first.'); return; }
    setBusy(true);
    try {
      await createHrDepartment(value);
      setName('');
      refresh();
    } catch (e: any) {
      // A failed create is never silent, and the typed name stays in the box. Plain words, never a database message.
      setCreateError(departmentErrorMessage(e, 'add', value));
    } finally {
      setBusy(false);
    }
  }

  function startEdit(d: { departmentId: string; name: string }) {
    setEditingId(d.departmentId);
    setEditName(d.name);
    setEditError(null);
  }
  function cancelEdit() {
    setEditingId(null);
    setEditName('');
    setEditError(null);
  }
  async function handleRename(departmentId: string) {
    const value = editName.trim();
    setEditError(null);
    if (!value) { setEditError('Enter a department name first.'); return; }
    setBusy(true);
    try {
      await renameHrDepartment(departmentId, value);
      cancelEdit();
      refresh();
    } catch (e: any) {
      setEditError(departmentErrorMessage(e, 'rename', value));
    } finally {
      setBusy(false);
    }
  }

  // Disable / Re-enable change nothing but whether the department is OFFERED for new choices: it keeps its id, its name and every person already in it.
  async function handleDisable(d: HrDepartment) {
    setRowError(null); setNotice(null);
    setBusy(true);
    try {
      await disableHrDepartment(d.departmentId);
      setConfirmDisableId(null);
      await refresh();
      setNotice(`“${d.name}” is disabled. People already in it keep it; it won’t be offered for new choices.`);
    } catch (e: any) {
      setRowError({ id: d.departmentId, text: departmentErrorMessage(e, 'disable', d.name) });
    } finally {
      setBusy(false);
    }
  }
  async function handleEnable(d: HrDepartment) {
    setRowError(null); setNotice(null);
    setBusy(true);
    try {
      await enableHrDepartment(d.departmentId);
      await refresh();
      setNotice(`“${d.name}” is enabled again and can be chosen for new people.`);
    } catch (e: any) {
      // The server's own explanation (another active department already uses this name) is shown as is; nothing was merged or renamed.
      setRowError({ id: d.departmentId, text: departmentErrorMessage(e, 'enable', d.name) });
    } finally {
      setBusy(false);
    }
  }
  const disabledCount = (departments ?? []).filter((d) => d.disabled).length;
  const visibleDepartments = (departments ?? []).filter((d) => showDisabled || !d.disabled);

  return (
    <div>
      <SectionHeading title="Organization" subtitle="Maintain departments and review reporting relationships." />
      <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-5 mb-4">
        <CardTitle action={
          <div className="flex gap-2 items-center">
            <label className="inline-flex items-center gap-1.5 text-xs text-gray-600 cursor-pointer whitespace-nowrap">
              <input type="checkbox" className="h-3.5 w-3.5" checked={showDisabled} onChange={(e) => setShowDisabled(e.target.checked)} data-testid="org-dept-show-disabled" />
              Show disabled{disabledCount > 0 ? <span className="text-gray-400">({disabledCount})</span> : null}
            </label>
            <input
              className="border border-gray-300 rounded px-3 py-1.5 text-sm"
              placeholder="Department name"
              value={name}
              maxLength={120}
              onChange={(e) => { setName(e.target.value); if (createError) setCreateError(null); }}
              onKeyDown={(e) => { if (e.key === 'Enter') handleCreate(); }}
              data-testid="org-dept-name"
            />
            <button onClick={handleCreate} disabled={busy || !name.trim()} className="bg-[#4AABDE] text-white text-sm px-4 py-1.5 rounded disabled:opacity-50" data-testid="org-dept-add">+ Department</button>
          </div>
        }>
          Departments
        </CardTitle>
        {createError && <div className="mt-2 text-sm text-red-700 bg-red-50 border border-red-200 rounded px-3 py-1.5" data-testid="org-dept-create-error">{createError}</div>}
        {loadError && (
          <div className="mt-2 flex items-center gap-3 text-sm text-red-700 bg-red-50 border border-red-200 rounded px-3 py-1.5" data-testid="org-dept-load-error">
            <span>{loadError}</span>
            <button type="button" onClick={() => refresh()} className="text-xs underline flex-shrink-0" data-testid="org-dept-load-retry">Retry</button>
          </div>
        )}
        {notice && <div role="status" className="mt-2 text-sm text-green-800 bg-green-50 border border-green-200 rounded px-3 py-1.5" data-testid="org-dept-notice">{notice}</div>}
        <ul className="divide-y divide-gray-100">
          {visibleDepartments.map((d) => (
            <li key={d.departmentId} className="py-2 text-sm text-gray-900" data-testid={`org-dept-row-${d.departmentId}`}>
              {editingId === d.departmentId ? (
                <div className="flex flex-col gap-1.5">
                  <div className="flex gap-2 items-center">
                    <input
                      className="border border-gray-300 rounded px-3 py-1.5 text-sm flex-1"
                      value={editName}
                      maxLength={120}
                      autoFocus
                      onChange={(e) => { setEditName(e.target.value); if (editError) setEditError(null); }}
                      onKeyDown={(e) => { if (e.key === 'Enter') handleRename(d.departmentId); if (e.key === 'Escape') cancelEdit(); }}
                      data-testid="org-dept-edit-input"
                    />
                    <button onClick={() => handleRename(d.departmentId)} disabled={busy || !editName.trim()} className="bg-[#4AABDE] text-white text-xs px-3 py-1.5 rounded disabled:opacity-50" data-testid="org-dept-save">Save</button>
                    <button onClick={cancelEdit} disabled={busy} className="text-gray-500 text-xs px-2 py-1.5 hover:underline" data-testid="org-dept-cancel">Cancel</button>
                  </div>
                  {editError && <div className="text-xs text-red-700" data-testid="org-dept-edit-error">{editError}</div>}
                </div>
              ) : d.disabled ? (
                <div className="flex items-center justify-between gap-3">
                  <span className="flex items-center gap-2 min-w-0">
                    <span className="text-gray-500 truncate">{d.name}</span>
                    <span className="text-[11px] bg-gray-100 text-gray-500 rounded px-1.5 py-0.5 flex-shrink-0" data-testid={`org-dept-disabled-badge-${d.departmentId}`}>Disabled</span>
                  </span>
                  <button type="button" onClick={() => handleEnable(d)} disabled={busy} className="text-xs text-[#4AABDE] hover:underline flex-shrink-0 disabled:opacity-50" data-testid={`org-dept-enable-${d.departmentId}`}>Re-enable</button>
                </div>
              ) : confirmDisableId === d.departmentId ? (
                <div className="flex flex-wrap items-center justify-between gap-2" role="group" aria-label={`Disable ${d.name}?`}>
                  <span className="text-xs text-gray-700">Disable “{d.name}”? People already in it keep it. It just won’t be offered for new choices, and you can re-enable it later.</span>
                  <span className="flex items-center gap-3 flex-shrink-0">
                    <button type="button" onClick={() => handleDisable(d)} disabled={busy} className="bg-[#4AABDE] text-white text-xs px-3 py-1.5 rounded disabled:opacity-50" data-testid={`org-dept-disable-confirm-${d.departmentId}`}>Disable</button>
                    <button type="button" onClick={() => setConfirmDisableId(null)} disabled={busy} className="text-gray-500 text-xs hover:underline" data-testid={`org-dept-disable-cancel-${d.departmentId}`}>Cancel</button>
                  </span>
                </div>
              ) : (
                <div className="flex items-center justify-between gap-3">
                  <span>{d.name}</span>
                  <span className="flex items-center gap-3 flex-shrink-0">
                    <button type="button" onClick={() => startEdit(d)} className="text-xs text-[#4AABDE] hover:underline" data-testid={`org-dept-edit-${d.departmentId}`}>Edit</button>
                    <button type="button" onClick={() => { setRowError(null); setNotice(null); setConfirmDisableId(d.departmentId); }} className="text-xs text-gray-500 hover:underline" data-testid={`org-dept-disable-${d.departmentId}`}>Disable</button>
                  </span>
                </div>
              )}
              {rowError && rowError.id === d.departmentId && <div role="alert" className="mt-1 text-xs text-red-700" data-testid={`org-dept-row-error-${d.departmentId}`}>{rowError.text}</div>}
            </li>
          ))}
          {departments && visibleDepartments.length === 0 && (
            <li className="py-2 text-sm text-gray-400">{departments.length === 0 ? 'No departments yet.' : 'No active departments. Tick “Show disabled” to see the disabled ones.'}</li>
          )}
        </ul>
      </div>

      <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-5">
        <CardTitle>Reporting relationships</CardTitle>
        {employees.length === 0 ? (
          <div className="text-sm text-gray-400 py-2">No HR records yet.</div>
        ) : (
          <ul className="divide-y divide-gray-100">
            {employees.map((e) => (
              <li key={e.employeeId} className="flex items-center justify-between gap-3 py-2.5">
                <div>
                  <div className="text-sm font-medium text-gray-900">{e.name ?? e.employeeId}</div>
                </div>
                <button type="button" onClick={() => navigate(`/hr/admin/employees/${e.employeeId}`)} className="text-xs text-[#4AABDE] hover:underline flex-shrink-0">
                  Open employee →
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      <details className="text-xs text-gray-500 mt-4">
        <summary className="text-[#4AABDE] cursor-pointer">How this page works</summary>
        <p className="mt-2 max-w-lg">
          Reporting relationships define potential team scope. HR permissions separately determine what someone can view or do. Change an employee's manager through the governed employment-change process on their own record.
        </p>
      </details>
    </div>
  );
}

// Owner-approved HR Administration layout (2026-10-07): "show friendly capability and scope
// labels plus plain-language explanation of included/excluded access. Exact capability codes
// may remain in expandable technical details. Do not change capability keys, scope mapping,
// grant/revoke payloads, authority or visibility." The underlying `capability`/`scope` strings
// sent to createHrAccessGrant/stored on each grant are UNCHANGED (workspace/accessLabels.ts is
// presentation-only) — this only swaps the <select> option TEXT and table-column text for
// friendly labels, and adds a per-grant "Technical details" disclosure showing the raw codes.
// ── Oct 20 — ONE PERSON, ONE CLEAR PANEL ──────────────────────────────────────────────────────────────────────────────────────────
// Pick a person, see all of their access together, change what you mean to change, press ONE "Save changes". The save is explicit and atomic (POST
// /hr/admin/access/employees/:id/changes): only the permissions you actually changed are sent, everything else the person holds is left alone, and
// "Saved" is shown only when the server confirmed the whole save. It uses the same grant rows, rules and audit as before; the server re-checks every
// change, so this screen offers nothing the server would refuse.
const SCOPE_RANK: Record<AccessScope, number> = { self: 0, direct_reports: 1, company: 2 };
type DesiredAccess = Record<string, { on: boolean; scope: AccessScope }>;

function newAccessRequestId(): string {
  const c: any = (globalThis as any).crypto;
  if (c && typeof c.randomUUID === 'function') return c.randomUUID();
  const hex = (n: number) => Array.from({ length: n }, () => Math.floor(Math.random() * 16).toString(16)).join('');
  return `${hex(8)}-${hex(4)}-4${hex(3)}-a${hex(3)}-${hex(12)}`;
}
/** The widest scope among a person's rows for one capability (the server treats the widest as the effective one), or null when they have none. */
function effectiveScopeOf(rows: HrAccessGrantRow[], employeeId: string, capability: string): AccessScope | null {
  const mine = rows.filter((g) => g.employeeId === employeeId && g.capability === capability);
  if (mine.length === 0) return null;
  return mine.reduce<AccessScope>((w, g) => (SCOPE_RANK[g.scope as AccessScope] > SCOPE_RANK[w] ? (g.scope as AccessScope) : w), mine[0].scope as AccessScope);
}
/** What the checkboxes show for a person: switched on at the scope they hold, or off with the permission's safest valid scope ready. */
function desiredFromGrants(rows: HrAccessGrantRow[], employeeId: string): DesiredAccess {
  const out: DesiredAccess = {};
  for (const p of EDITABLE_PERMISSIONS) {
    const cur = effectiveScopeOf(rows, employeeId, p.capability);
    out[p.capability] = { on: cur !== null, scope: cur ?? p.scopes[0] };
  }
  return out;
}
/** Only the permissions whose checkbox or scope differs from what the person holds. Owner-only ones are never sent by a non-Owner. */
function computeAccessChanges(rows: HrAccessGrantRow[], employeeId: string, desired: DesiredAccess, isPrimaryOwner: boolean): HrAccessChange[] {
  const changes: HrAccessChange[] = [];
  for (const p of EDITABLE_PERMISSIONS) {
    if (p.ownerOnly && !isPrimaryOwner) continue;
    const cur = effectiveScopeOf(rows, employeeId, p.capability);
    const d = desired[p.capability];
    const want: AccessScope | null = d && d.on ? d.scope : null;
    if (want !== cur) changes.push({ capability: p.capability, scope: want });
  }
  return changes;
}
const EDITABLE_CAPABILITY_CODES = EDITABLE_PERMISSIONS.map((p) => p.capability);

function AccessPanel({ isPrimaryOwner, viewerEmployeeId }: { isPrimaryOwner: boolean; viewerEmployeeId: string }) {
  const [grants, setGrants] = useState<HrAccessGrantRow[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [names, setNames] = useState<Record<string, string | null>>({});
  const [selectedId, setSelectedId] = useState('');
  // ONLY what the user changed on this screen. Every permission they did not touch always follows the server's current access, so a reload after someone
  // else's change can never turn into a silent revert of it on the next Save.
  const [edits, setEdits] = useState<DesiredAccess>({});
  const [requestId, setRequestId] = useState(newAccessRequestId);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ kind: 'ok' | 'error' | 'info'; text: string } | null>(null);
  const [pendingSwitch, setPendingSwitch] = useState<string | null>(null);

  const refresh = (): Promise<HrAccessGrantRow[] | null> => {
    setLoadError(null);
    return listHrAccessGrants()
      .then((d) => { setGrants(d.grants); return d.grants as HrAccessGrantRow[]; })
      .catch((e: any) => { setLoadError(e?.message || 'Could not load access. Please try again.'); return null; });
  };
  useEffect(() => {
    refresh();
    listHrAdminEmployees()
      .then((d) => setNames(Object.fromEntries(d.employees.map((e) => [e.employeeId, e.name]))))
      .catch(() => setNames({}));
  }, []);

  const rows = grants ?? [];
  const isSelf = selectedId !== '' && selectedId === viewerEmployeeId;
  const desired: DesiredAccess = selectedId ? { ...desiredFromGrants(rows, selectedId), ...edits } : {};
  const changes = selectedId && !isSelf ? computeAccessChanges(rows, selectedId, desired, isPrimaryOwner) : [];
  const dirty = changes.length > 0;
  const baselineGrantIds = rows.filter((g) => g.employeeId === selectedId && EDITABLE_CAPABILITY_CODES.includes(g.capability)).map((g) => g.grantId);
  const otherAccess = rows.filter((g) => g.employeeId === selectedId && !EDITABLE_CAPABILITY_CODES.includes(g.capability));
  const personName = (id: string) => resolveDisplayName(id, names);

  function selectPerson(id: string) {
    setSelectedId(id);
    setEdits({});
    setRequestId(newAccessRequestId());
    setMessage(null);
    setPendingSwitch(null);
  }
  function requestSelect(id: string) {
    if (!id || id === selectedId) return;       // the picker reports '' while typing: ignore it
    if (dirty) { setPendingSwitch(id); return; } // never drop unsaved changes silently
    selectPerson(id);
  }
  function edit(capability: string, next: { on: boolean; scope: AccessScope }) { setEdits((e) => ({ ...e, [capability]: next })); setRequestId(newAccessRequestId()); setMessage(null); }
  function toggle(capability: string, on: boolean) { edit(capability, { ...desired[capability], on }); }
  function setScope(capability: string, scope: AccessScope) { edit(capability, { ...desired[capability], scope }); }
  function discard() { setEdits({}); setRequestId(newAccessRequestId()); setMessage(null); }

  function describeChange(c: HrAccessChange): string {
    const cur = effectiveScopeOf(rows, selectedId, c.capability);
    const label = editableShortLabel(c.capability);
    if (c.scope === null) return `Turn off “${label}”`;
    const to = editableScopeLabel(c.capability, c.scope);
    return cur === null ? `Turn on “${label}” — ${to}` : `Change “${label}” to ${to}`;
  }
  function explain(e: any): string {
    if (e?.status === 403) return accessRefusalMessage(String(e.message || ''));
    if (e?.status === 404) return 'That person or action isn’t available to you.';
    if (e?.status === 400) return String(e.message || 'That change isn’t allowed.');
    return 'Could not save. Nothing was changed on this screen. Try again.';
  }

  async function save() {
    if (!dirty || saving || !selectedId) return;
    const sent = changes;
    setSaving(true);
    setMessage(null);
    try {
      const res = await applyHrAccessChanges(selectedId, { requestId, baselineGrantIds, changes: sent });
      // Take the SERVER's word for the saved state, replacing only this person's rows; then say "Saved".
      const nextRows = [...rows.filter((g) => g.employeeId !== selectedId), ...res.grants];
      setGrants(nextRows);
      setEdits({});
      setRequestId(newAccessRequestId());
      // The confirmation names the person. It is shown only after the server confirmed the whole save.
      setMessage({ kind: 'ok', text: res.replay ? `Access updated for ${personName(selectedId)}. These changes had already been saved.` : `Access updated for ${personName(selectedId)}.` });
    } catch (e: any) {
      // Whatever went wrong, find out what is REALLY saved before saying anything: a lost reply can hide a save that did happen.
      const fresh = await refresh();
      if (e?.status === 409) {
        setMessage({ kind: 'error', text: String(e.message || 'This person’s access changed while you were editing. Nothing was saved. Review and save again.') });
      } else if (fresh && computeAccessChanges(fresh, selectedId, { ...desiredFromGrants(fresh, selectedId), ...edits }, isPrimaryOwner).length === 0) {
        setEdits({});
        setRequestId(newAccessRequestId());
        setMessage({ kind: 'ok', text: `Access updated for ${personName(selectedId)}. The reply was lost, but reloading shows your changes are in place.` });
      } else {
        setMessage({ kind: 'error', text: `${explain(e)} Your edits are still here.` });
      }
    } finally {
      setSaving(false);
    }
  }

  return (
    <div>
      <div className="mb-5">
        <h1 className="text-xl font-bold text-gray-900 leading-tight">Access permissions</h1>
        <p className="text-sm text-gray-500 mt-1">Choose a person, see everything they can do in HR, and change it with one Save.</p>
      </div>

      <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-5 mb-4">
        <CardTitle>Person</CardTitle>
        <div className="flex gap-2 flex-wrap items-center">
          <EmployeePicker value={selectedId} onChange={requestSelect} placeholder="Search for a person…" />
        </div>
        {pendingSwitch && (
          <div role="alertdialog" aria-label="Unsaved changes" data-testid="access-switch-warning" className="mt-3 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900 flex flex-wrap items-center gap-3">
            <span>You have unsaved changes for {personName(selectedId)}.</span>
            <button type="button" onClick={() => selectPerson(pendingSwitch)} className="underline" data-testid="access-switch-discard">Discard them and open {personName(pendingSwitch)}</button>
            <button type="button" onClick={() => setPendingSwitch(null)} className="underline" data-testid="access-switch-keep">Keep editing</button>
          </div>
        )}
      </div>

      {loadError && (
        <div role="alert" data-testid="access-load-error" className="mb-4 flex items-center gap-3 text-sm text-red-700 bg-red-50 border border-red-200 rounded px-3 py-1.5">
          <span>{loadError}</span>
          <button type="button" onClick={() => refresh()} className="text-xs underline flex-shrink-0">Retry</button>
        </div>
      )}

      {selectedId && grants !== null && (
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-5 mb-4" data-testid="access-editor">
          <CardTitle>{personName(selectedId)}</CardTitle>

          {isSelf ? (
            <div className="text-sm text-gray-700 mb-3" data-testid="access-self-note">
              <p>This is you. Nobody can change their own access here{isPrimaryOwner ? '' : '; ask the Owner or another access administrator'}.</p>
              {isPrimaryOwner && (
                <p className="mt-2 rounded-md bg-[#EDF5FC] px-3 py-2 text-xs text-gray-700" data-testid="access-owner-built-in">
                  <strong>Primary Owner.</strong> You hold every HR permission for the whole company. This is built in, not a saved setting, so there is nothing here to switch off.
                </p>
              )}
            </div>
          ) : (
            <p className="text-xs text-gray-500 mb-3">Tick what this person should be able to do. Nothing changes until you press Save changes.</p>
          )}

          {/* Oct 20 final UAT batch: ONE compact row per permission (checkbox, short label, who it covers, expand arrow). Same state, same Save. */}
          <ul className="divide-y divide-gray-100" aria-label={`HR permissions for ${personName(selectedId)}`}>
            {EDITABLE_PERMISSIONS.map((p) => {
              const d = desired[p.capability] ?? { on: false, scope: p.scopes[0] };
              return (
                <PermissionRow
                  key={p.capability} perm={p} on={d.on} scope={d.scope} heldScope={effectiveScopeOf(rows, selectedId, p.capability)}
                  locked={isSelf || (!!p.ownerOnly && !isPrimaryOwner)} saving={saving} ownerOnlyLocked={!!p.ownerOnly && !isPrimaryOwner}
                  onToggle={(on) => toggle(p.capability, on)} onScope={(scope) => setScope(p.capability, scope)}
                />
              );
            })}
          </ul>

          {otherAccess.length > 0 && (
            <div className="mt-4 pt-3 border-t border-gray-100" data-testid="access-other">
              <h3 className="text-xs font-semibold text-gray-700">Other access on file (not changed here)</h3>
              <ul className="mt-1 space-y-0.5">
                {otherAccess.map((g) => (
                  <li key={g.grantId} className="text-xs text-gray-600">{capabilityLabel(g.capability)} · {scopeLabel(g.scope)}</li>
                ))}
              </ul>
              <p className="text-xs text-gray-400 mt-1">These stay exactly as they are when you save.</p>
            </div>
          )}

          {!isSelf && (
            <div className="mt-4 pt-3 border-t border-gray-100">
              {dirty && (
                <ul className="mb-3 text-xs text-gray-700 list-disc pl-5 space-y-0.5" data-testid="access-change-summary" aria-label="Changes waiting to be saved">
                  {changes.map((c) => <li key={c.capability}>{describeChange(c)}</li>)}
                </ul>
              )}
              <div className="flex items-center gap-3 flex-wrap">
                <button
                  type="button" onClick={save} disabled={!dirty || saving} data-testid="access-save"
                  className="bg-[#4AABDE] hover:bg-[#3a94c8] text-white text-sm font-medium px-4 py-1.5 rounded disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {saving ? 'Saving…' : 'Save changes'}
                </button>
                <button type="button" onClick={discard} disabled={!dirty || saving} data-testid="access-discard" className="text-sm text-gray-600 hover:underline disabled:opacity-40 disabled:no-underline">Discard changes</button>
                {!dirty && !message && <span className="text-xs text-gray-400">No changes yet.</span>}
              </div>
            </div>
          )}
          {message && (
            <div
              role={message.kind === 'error' ? 'alert' : 'status'} data-testid={`access-message-${message.kind}`}
              className={`mt-3 text-sm rounded px-3 py-2 border ${message.kind === 'ok' ? 'text-green-800 bg-green-50 border-green-200' : message.kind === 'error' ? 'text-red-700 bg-red-50 border-red-200' : 'text-gray-700 bg-gray-50 border-gray-200'}`}
            >
              {message.text}
            </div>
          )}
        </div>
      )}

      <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-5">
        <CardTitle>People with access</CardTitle>
        {grants === null ? (
          <div className="text-sm text-gray-400 py-2">{loadError ? '' : 'Loading…'}</div>
        ) : (
          // One row per person with a badge for each permission they ACTUALLY hold; expand a row for the reach and whether it is a saved setting or built in.
          <AccessSummary
            rows={rows} builtInOwnerId={isPrimaryOwner ? viewerEmployeeId : null} viewerEmployeeId={viewerEmployeeId}
            personName={personName} onEdit={requestSelect}
          />
        )}
      </div>

      <AboutHrAccess />
    </div>
  );
}

// Owner-approved HR Administration layout (2026-10-07): "template library first; open/new
// template as focused editor; edit task with explicit labels What needs to be done / Who is
// responsible / When is it due / Number of days. Display before/on/after hire date for
// onboarding, separation date for offboarding. Preserve underlying due-offset meaning,
// including zero and negative values. This is a presentation mapping, not a new scheduling/
// date/calendar rule." Rebuilt from the prior single combined page (form always visible above a
// flat list) into: a template-library list first, a "+ New template" button, and a focused
// per-template editor (task list + a dedicated "Edit task"/"Add task" sub-view) reached by
// opening a template — same createHrChecklistTemplate/updateHrChecklistTemplateTasks/
// retireHrChecklistTemplate calls, same editingId-disables-name/processKind constraint, same
// dueOffsetDays field end-to-end (now presented via a before/on/after <select> + a conditional
// "Number of days" input instead of one signed-integer <input type=number>, but the VALUE
// written back is still the identical signed integer: on=0, before=-N, after=+N — the mapping
// function below is the ONLY new logic, everything else is unchanged data flow).
//
// Responsibility labels moved OUT to their own route/page (ResponsibilityLabelsPage, below) per
// the decision's "Keep responsibility-label management separate" — same
// list/create/rename/retire calls, unchanged.

/** before/on/after + days (UI-only) <-> signed dueOffsetDays (the real, unchanged field). */
function offsetToUi(dueOffsetDays: number): { when: 'before' | 'on' | 'after'; days: number } {
  if (dueOffsetDays === 0) return { when: 'on', days: 0 };
  if (dueOffsetDays < 0) return { when: 'before', days: Math.abs(dueOffsetDays) };
  return { when: 'after', days: dueOffsetDays };
}
function uiToOffset(when: 'before' | 'on' | 'after', days: number): number {
  if (when === 'on') return 0;
  const n = Math.max(1, Math.round(days) || 1);
  return when === 'before' ? -n : n;
}
function describeDue(dueOffsetDays: number, processKind: 'onboarding' | 'offboarding'): string {
  const anchor = processKind === 'offboarding' ? 'separation date' : 'hire date';
  const { when, days } = offsetToUi(dueOffsetDays);
  if (when === 'on') return `On ${anchor}`;
  return `${days} ${days === 1 ? 'day' : 'days'} ${when} ${anchor}`;
}

interface TemplateTaskDraft { taskText: string; responsibleRole: string; dueOffsetDays: number }

/** Move one item to a new position (pure; returns the same array when the move is meaningless). */
function moveItem<T>(list: T[], from: number, to: number): T[] {
  if (from === to || from < 0 || to < 0 || from >= list.length || to >= list.length) return list;
  const next = [...list];
  const [item] = next.splice(from, 1);
  next.splice(to, 0, item);
  return next;
}
/** Longest optional template "Short description" (matches the server and the migration 206 CHECK): it shows on one line of the library. */
const SHORT_DESCRIPTION_MAX = 120;
/** Longest name accepted when an EXISTING template is renamed (matches the server). */
const TEMPLATE_NAME_MAX = 120;
function sameTemplateTasks(a: TemplateTaskDraft[], b: TemplateTaskDraft[]): boolean {
  return a.length === b.length && a.every((t, i) => t.taskText === b[i].taskText && t.responsibleRole === b[i].responsibleRole && t.dueOffsetDays === b[i].dueOffsetDays);
}

function ChecklistTemplatesPanel() {
  const navigate = useNavigate();
  // Oct 23: when the library was opened from an employee's Onboarding / Offboarding tab, a one-click way back to that employee (router state only; nothing is stored).
  const cameFrom = (useLocation().state as { returnTo?: string; employeeName?: string } | null) ?? null;
  const [templates, setTemplates] = useState<HrChecklistTemplate[] | null>(null);
  const [responsibilityOptions, setResponsibilityOptions] = useState<HrChecklistResponsibilityOption[]>([]);
  const [openTemplateId, setOpenTemplateId] = useState<string | 'new' | null>(null);
  // `shortDescription` is the optional one-line note (migration 206) as typed; '' means none.
  const [draft, setDraft] = useState<{ name: string; processKind: 'onboarding' | 'offboarding'; shortDescription: string; tasks: TemplateTaskDraft[] }>({
    name: '', processKind: 'onboarding', shortDescription: '', tasks: [],
  });
  // What the editor opened with: "unchanged" means draft equals this, and an unchanged Close leaves at once.
  const [baseline, setBaseline] = useState<{ name: string; processKind: 'onboarding' | 'offboarding'; shortDescription: string; tasks: TemplateTaskDraft[] }>({ name: '', processKind: 'onboarding', shortDescription: '', tasks: [] });
  const [status, setStatus] = useState<string | null>(null);
  // A confirmation shown in the library ONLY after the server confirmed a save (the await resolved).
  const [notice, setNotice] = useState<string | null>(null);
  // Oct 20: retired templates are hidden by default; the checkbox reveals them (Retired badge, Restore).
  const [showRetired, setShowRetired] = useState(false);
  const [libraryError, setLibraryError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [closePrompt, setClosePrompt] = useState(false);
  const [taskEditIndex, setTaskEditIndex] = useState<number | 'new' | null>(null);
  const [taskDraft, setTaskDraft] = useState<TemplateTaskDraft>({ taskText: '', responsibleRole: '', dueOffsetDays: 1 });
  const [dragFrom, setDragFrom] = useState<number | null>(null);
  const [dragOver, setDragOver] = useState<number | null>(null);
  const [announce, setAnnounce] = useState('');

  // Oct 24: loading / ready / denied (the server refused the capability-gated list) / unavailable are distinct. A failed FIRST read is never drawn as "No templates yet"
  // or "No options configured yet"; a failed RE-read keeps what is already on screen.
  const [templatesView, setTemplatesView] = useState<LoadView>({ kind: 'loading' });
  const [optionsView, setOptionsView] = useState<LoadView>({ kind: 'loading' });
  const mayManageTemplates = useSurfaceFlag('mayManageChecklistTemplates');
  const cannotManageTemplates = mayManageTemplates === false;
  const templatesPermissionMessage = needsPermissionMessage(PERMISSION_LABEL.checklistTemplates, 'change checklist templates');
  const refresh = () => listHrChecklistTemplates()
    .then((d) => { setTemplates(d.templates); setTemplatesView({ kind: 'ready' }); })
    .catch((e) => setTemplatesView((v) => (v.kind === 'ready' ? v : classifyLoadError(e, { gatedList: true }))));
  const refreshOptions = () =>
    // NEEDS DEV DEPLOY: migration 085 + the responsibility-options routes have not been
    // deployed to real DEV this pass. A failure no longer pretends the list is empty: it is shown as denied / unavailable.
    listHrChecklistResponsibilityOptions()
      .then((d) => { setResponsibilityOptions(d.options); setOptionsView({ kind: 'ready' }); })
      .catch((e) => { setResponsibilityOptions([]); setOptionsView(classifyLoadError(e, { gatedList: true })); });
  useEffect(() => { refresh(); refreshOptions(); }, []);

  const openTemplate = openTemplateId && openTemplateId !== 'new' ? (templates ?? []).find((t) => t.templateId === openTemplateId) ?? null : null;
  const isNew = openTemplateId === 'new';

  function startNew() {
    const fresh = { name: 'New onboarding template', processKind: 'onboarding' as const, shortDescription: '', tasks: [] as TemplateTaskDraft[] };
    setDraft(fresh);
    setBaseline(fresh);
    setOpenTemplateId('new');
    setTaskEditIndex(null);
    setClosePrompt(false);
    setStatus(null);
    setNotice(null);
  }
  function startOpen(t: HrChecklistTemplate) {
    const opened = { name: t.name, processKind: t.processKind, shortDescription: t.shortDescription ?? '', tasks: t.tasks.map((x) => ({ taskText: x.taskText, responsibleRole: x.responsibleRole, dueOffsetDays: x.dueOffsetDays })) };
    setDraft(opened);
    setBaseline(opened);
    setOpenTemplateId(t.templateId);
    setTaskEditIndex(null);
    setClosePrompt(false);
    setStatus(null);
    setNotice(null);
  }
  function closeEditor() {
    setOpenTemplateId(null);
    setTaskEditIndex(null);
    setClosePrompt(false);
    setStatus(null);
  }

  // The open task editor counts as unsaved work when it holds something different from the task it was opened on.
  const taskEditDirty = taskEditIndex !== null && (
    taskEditIndex === 'new'
      ? taskDraft.taskText.trim() !== ''
      : (() => { const o = draft.tasks[taskEditIndex]; return !o || o.taskText !== taskDraft.taskText || o.responsibleRole !== taskDraft.responsibleRole || o.dueOffsetDays !== taskDraft.dueOffsetDays; })()
  );
  // Whitespace-only differences (a trailing space, a doubled space) are not a change to the description.
  const normDescription = (s: string) => s.replace(/\s+/g, ' ').trim();
  const descriptionDirty = normDescription(draft.shortDescription) !== normDescription(baseline.shortDescription);
  // A rename of an EXISTING template (a new template names itself in the create form). Whitespace-only differences are not a rename.
  const nameDirty = !isNew && normDescription(draft.name) !== normDescription(baseline.name);
  const templateDirty = draft.name !== baseline.name || draft.processKind !== baseline.processKind || descriptionDirty || !sameTemplateTasks(draft.tasks, baseline.tasks);
  const anyDirty = templateDirty || taskEditDirty;

  /** Close (X or Close): nothing unsaved leaves at once; otherwise ask - Save and close, Discard changes, or Keep editing. */
  function requestClose() {
    if (saving) return;
    if (!anyDirty) { closeEditor(); return; }
    setClosePrompt(true);
  }

  function startTaskEdit(index: number | 'new') {
    if (index === 'new') {
      setTaskDraft({ taskText: '', responsibleRole: responsibilityOptions[0]?.label ?? '', dueOffsetDays: 1 });
    } else {
      setTaskDraft({ ...draft.tasks[index] });
    }
    setTaskEditIndex(index);
    setStatus(null);
  }
  function applyTaskEdit() {
    if (!taskDraft.taskText.trim()) { setStatus('Enter what needs to be done.'); return; }
    setDraft((d) => {
      const tasks = [...d.tasks];
      const clean = { ...taskDraft, taskText: taskDraft.taskText.trim() };
      if (taskEditIndex === 'new') tasks.push(clean);
      else if (typeof taskEditIndex === 'number') tasks[taskEditIndex] = clean;
      return { ...d, tasks };
    });
    setTaskEditIndex(null);
    setStatus(null);
  }
  function cancelTaskEdit() { setTaskEditIndex(null); setStatus(null); }

  /** Reorder (drag or Move up / Move down). Disabled while a task is open for editing so positions cannot shift under it. */
  function moveTask(from: number, to: number) {
    if (taskEditIndex !== null || from === to) return;
    const title = draft.tasks[from]?.taskText ?? 'Task';
    const target = Math.max(0, Math.min(draft.tasks.length - 1, to));
    setDraft((d) => ({ ...d, tasks: moveItem(d.tasks, from, target) }));
    setAnnounce(`Moved “${title}” to position ${target + 1} of ${draft.tasks.length}.`);
  }

  /** Save and close. Nothing is reported as saved, and the editor does not close, until the server confirmed it; a failure keeps every edit. */
  async function handleSaveAndClose() {
    if (saving) return;
    if (!draft.name.trim()) { setStatus('Enter a template name.'); setClosePrompt(false); return; }
    // An open task editor is part of the work: fold it in (a blank NEW task is simply dropped; a blank existing one is an error).
    let tasks = draft.tasks;
    if (taskEditIndex !== null) {
      if (taskDraft.taskText.trim()) {
        const clean = { ...taskDraft, taskText: taskDraft.taskText.trim() };
        tasks = taskEditIndex === 'new' ? [...tasks, clean] : tasks.map((t, i) => (i === taskEditIndex ? clean : t));
      } else if (taskEditIndex !== 'new') {
        setStatus('Every task needs a description. Finish or cancel the task you have open.'); setClosePrompt(false); return;
      }
    }
    if (!tasks.length) { setStatus('Add at least one task before saving.'); setClosePrompt(false); return; }
    if (tasks.some((t) => !t.taskText.trim())) { setStatus('Every task needs a description.'); setClosePrompt(false); return; }
    if (normDescription(draft.shortDescription).length > SHORT_DESCRIPTION_MAX) { setStatus(`Short description must be ${SHORT_DESCRIPTION_MAX} characters or fewer.`); setClosePrompt(false); return; }
    setSaving(true);
    setStatus(null);
    try {
      const payload = tasks.map((t, i) => ({ ...t, sortOrder: i }));
      const description = normDescription(draft.shortDescription) || null; // empty = none
      if (isNew) await createHrChecklistTemplate({ name: draft.name.trim(), processKind: draft.processKind, shortDescription: description, tasks: payload });
      // On an existing template the description is sent only when it changed; otherwise the server leaves it exactly as it is.
      // The new name is sent only when it was renamed; otherwise the server leaves the name exactly as it is.
      else if (openTemplateId) await updateHrChecklistTemplateTasks(openTemplateId, payload, descriptionDirty ? description : undefined, nameDirty ? normDescription(draft.name) : undefined);
      // Re-read the library BEFORE leaving the editor, so the list never flashes the old name/description/count after the "Saved" notice.
      await refresh();
      setNotice(`Saved “${draft.name.trim()}”. It applies to checklists assigned from now on; existing employee checklists are unchanged.`);
      closeEditor();
    } catch (e: any) {
      // Every edit stays exactly as the user left it (including the open task editor).
      setClosePrompt(false);
      setStatus(`Not saved. ${actionErrorMessage(e, PERMISSION_LABEL.checklistTemplates, 'save checklist templates', 'The template could not be saved.')} Your changes are still here.`);
    } finally {
      setSaving(false);
    }
  }

  // Oct 20 final UAT batch: Retire takes the template out of the default (active) view and says so; Restore brings the SAME template back (identity, tasks, order and
  // description kept; already-assigned checklists are never rewritten). Each confirmation is shown only after the server confirmed it.
  async function handleRetire(t: HrChecklistTemplate) {
    setLibraryError(null); setNotice(null);
    try {
      await retireHrChecklistTemplate(t.templateId);
      // Re-read FIRST, then confirm: the row leaves the default view and the confirmation appears together, never the other way round.
      await refresh();
      setNotice(`“${t.name}” was retired. It is no longer offered for new checklists; checklists already assigned are not affected. Tick “Show inactive” to restore it later.`);
    } catch (e: any) {
      setLibraryError(e?.status === 403 ? `“${t.name}” was not retired. ${templatesPermissionMessage}` : `“${t.name}” couldn’t be retired right now. Nothing was changed. Please try again.`);
    }
  }
  async function handleRestore(t: HrChecklistTemplate) {
    setLibraryError(null); setNotice(null);
    try {
      await restoreHrChecklistTemplate(t.templateId);
      await refresh();
      setNotice(`“${t.name}” was restored and can be assigned again. Checklists already assigned are not affected.`);
    } catch (e: any) {
      setLibraryError(e?.status === 403 ? `“${t.name}” was not restored. ${templatesPermissionMessage}` : `“${t.name}” couldn’t be restored right now. Nothing was changed. Please try again.`);
    }
  }

  // ── Focused template editor (template library opens into this, matching "template library
  //    first; open/new template as focused editor") ──────────────────────────────────────────
  if (openTemplateId !== null) {
    const title = isNew ? draft.name || 'New template' : openTemplate?.name ?? draft.name;
    const count = draft.tasks.length;
    const editing = taskEditIndex !== null;
    const anchorWord = draft.processKind === 'offboarding' ? 'separation' : 'hire';
    // The task being edited may carry a label that is no longer in the managed list (renamed or retired later); keep it selectable so an untouched task never loses it.
    const activeLabels = responsibilityOptions.map((o) => o.label);
    const heldLabel = taskDraft.responsibleRole;
    const heldIsRetired = !!heldLabel && !activeLabels.some((l) => l.toLowerCase() === heldLabel.toLowerCase());

    // One editor body, rendered inside the row being edited (or after the list for a new task).
    const taskEditor = (
      <div className="rounded-md border border-[#4AABDE]/40 bg-[#F5FAFE] p-3 space-y-3" data-testid="checklist-task-editor">
        <div>
          <label className="text-xs text-gray-600 block mb-1">What needs to be done?</label>
          <input
            className="border border-gray-300 rounded px-3 py-1.5 text-sm w-full bg-white" placeholder="e.g. Set up work email" autoFocus
            value={taskDraft.taskText} onChange={(e) => setTaskDraft((d) => ({ ...d, taskText: e.target.value }))}
          />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-xs text-gray-600 block mb-1">Who is responsible?</label>
            <select
              className="border border-gray-300 rounded px-3 py-1.5 text-sm w-full bg-white" value={taskDraft.responsibleRole}
              onChange={(e) => setTaskDraft((d) => ({ ...d, responsibleRole: e.target.value }))}
            >
              {/* Oct 24: "No options configured yet" only after a SUCCESSFUL, authorized, empty list. */}
              {responsibilityOptions.length === 0 && !heldLabel && (
                <option value="">{optionsView.kind === 'ready' ? 'No options configured yet' : optionsView.kind === 'loading' ? 'Loading…' : optionsView.kind === 'denied' ? 'Not available to you' : 'Could not be loaded'}</option>
              )}
              {heldIsRetired && <option value={heldLabel}>{heldLabel} (no longer in the list)</option>}
              {responsibilityOptions.map((o) => <option key={o.optionId} value={o.label}>{o.label}</option>)}
            </select>
            {(optionsView.kind === 'denied' || optionsView.kind === 'unavailable') && (
              <div className="mt-1"><LoadViewNotice view={optionsView} thing="responsibility labels" permission={PERMISSION_LABEL.checklistTemplates} onRetry={refreshOptions} testIdPrefix="checklist-labels" /></div>
            )}
          </div>
          <div>
            <label className="text-xs text-gray-600 block mb-1">When is it due?</label>
            <select
              className="border border-gray-300 rounded px-3 py-1.5 text-sm w-full bg-white" value={offsetToUi(taskDraft.dueOffsetDays).when}
              onChange={(e) => {
                const when = e.target.value as 'before' | 'on' | 'after';
                const days = offsetToUi(taskDraft.dueOffsetDays).days || 1;
                setTaskDraft((d) => ({ ...d, dueOffsetDays: uiToOffset(when, days) }));
              }}
            >
              <option value="before">Before {anchorWord} date</option>
              <option value="on">On {anchorWord} date</option>
              <option value="after">After {anchorWord} date</option>
            </select>
          </div>
        </div>
        {offsetToUi(taskDraft.dueOffsetDays).when !== 'on' && (
          <div>
            <label className="text-xs text-gray-600 block mb-1">Number of days</label>
            <input
              type="number" min={1} step={1} className="border border-gray-300 rounded px-3 py-1.5 text-sm w-32 bg-white"
              value={offsetToUi(taskDraft.dueOffsetDays).days}
              onChange={(e) => { const when = offsetToUi(taskDraft.dueOffsetDays).when; setTaskDraft((d) => ({ ...d, dueOffsetDays: uiToOffset(when, Number(e.target.value)) })); }}
            />
          </div>
        )}
        <p className="text-xs text-gray-500">Due: {describeDue(taskDraft.dueOffsetDays, draft.processKind)}</p>
        <div className="flex items-center gap-2">
          <button type="button" onClick={applyTaskEdit} disabled={!taskDraft.responsibleRole && optionsView.kind !== 'ready'} title={!taskDraft.responsibleRole && optionsView.kind !== 'ready' ? 'The responsibility labels have not loaded, so a responsible person cannot be chosen yet.' : undefined} className="bg-[#4AABDE] text-white text-sm px-4 py-1.5 rounded disabled:opacity-50 disabled:cursor-not-allowed" data-testid="checklist-task-done">{taskEditIndex === 'new' ? 'Add this task' : 'Done'}</button>
          <button type="button" onClick={cancelTaskEdit} className="text-sm text-gray-600 px-3 py-1.5 hover:underline" data-testid="checklist-task-cancel">Cancel</button>
          <span className="text-xs text-gray-400">Nothing is saved to the template until you press Save and close.</span>
        </div>
      </div>
    );

    return (
      <div data-testid="checklist-editor">
        <div className="flex items-start justify-between gap-3 mb-5">
          <div className="min-w-0">
            <h1 className="text-xl font-bold text-gray-900 leading-tight">{title}</h1>
            <p className="text-sm text-gray-500 mt-1">{draft.processKind === 'offboarding' ? 'Offboarding' : 'Onboarding'} · {count} task{count === 1 ? '' : 's'}</p>
          </div>
          <button
            type="button" onClick={requestClose} aria-label="Close template editor" data-testid="checklist-close-x"
            className="flex-shrink-0 rounded-md p-1.5 text-gray-500 hover:bg-gray-100 hover:text-gray-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#4AABDE]"
          >
            <svg width="18" height="18" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true"><path d="M5 5l10 10M15 5L5 15" /></svg>
          </button>
        </div>

        {closePrompt && (
          <div role="alertdialog" aria-label="Unsaved changes" data-testid="checklist-close-prompt" className="mb-4 rounded-md border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900">
            <p className="font-medium">You have unsaved changes to this template.</p>
            <div className="mt-2 flex flex-wrap items-center gap-3">
              <button type="button" onClick={handleSaveAndClose} disabled={saving || cannotManageTemplates} title={cannotManageTemplates ? templatesPermissionMessage : undefined} className="bg-[#4AABDE] text-white px-3 py-1.5 rounded disabled:opacity-50" data-testid="checklist-prompt-save">Save and close</button>
              <button type="button" onClick={closeEditor} disabled={saving} className="underline" data-testid="checklist-prompt-discard">Discard changes</button>
              <button type="button" onClick={() => setClosePrompt(false)} className="underline" data-testid="checklist-prompt-keep">Keep editing</button>
            </div>
          </div>
        )}

        <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-5 mb-4">
          {isNew && (
            <div className="grid grid-cols-2 gap-3 mb-4">
              <div>
                <label className="text-xs text-gray-500 block mb-1">Template name</label>
                <input className="border border-gray-300 rounded px-3 py-1.5 text-sm w-full" value={draft.name} onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))} />
              </div>
              <div>
                <label className="text-xs text-gray-500 block mb-1">Checklist type</label>
                <select className="border border-gray-300 rounded px-3 py-1.5 text-sm w-full" value={draft.processKind} onChange={(e) => setDraft((d) => ({ ...d, processKind: e.target.value as any }))}>
                  <option value="onboarding">Onboarding</option>
                  <option value="offboarding">Offboarding</option>
                </select>
              </div>
            </div>
          )}
          {/* Rename (Oct 20, Owner request): an EXISTING template's name can be changed here. The type stays as created. Checklists already assigned keep the name they were assigned under. */}
          {!isNew && (
            <div className="mb-4">
              <label htmlFor="checklist-template-name" className="text-xs text-gray-500 block mb-1">Template name</label>
              <input
                id="checklist-template-name" data-testid="checklist-template-name"
                className="border border-gray-300 rounded px-3 py-1.5 text-sm w-full" maxLength={TEMPLATE_NAME_MAX}
                value={draft.name} onChange={(e) => { setDraft((d) => ({ ...d, name: e.target.value })); if (status) setStatus(null); }}
                aria-describedby="checklist-template-name-help"
              />
              <p id="checklist-template-name-help" className="text-xs text-gray-400 mt-1">
                Renaming changes what new checklists are called. Checklists already assigned keep the name they were assigned under.
              </p>
            </div>
          )}
          {/* Optional one-liner (Owner visual pass): shown in parentheses after the task count in the library so people can see what is involved and why to use this template. */}
          <div className="mb-4">
            <label htmlFor="checklist-short-description" className="text-xs text-gray-500 block mb-1">Short description <span className="text-gray-400">(optional)</span></label>
            <input
              id="checklist-short-description" data-testid="checklist-short-description"
              className="border border-gray-300 rounded px-3 py-1.5 text-sm w-full" maxLength={SHORT_DESCRIPTION_MAX}
              placeholder="e.g. Standard first-week setup for office staff: accounts, equipment, payroll forms"
              value={draft.shortDescription} onChange={(e) => setDraft((d) => ({ ...d, shortDescription: e.target.value }))}
              aria-describedby="checklist-short-description-help"
            />
            <p id="checklist-short-description-help" className="text-xs text-gray-400 mt-1">
              Shown in parentheses after the task count in the template list: what is involved and why to use it. {normDescription(draft.shortDescription).length}/{SHORT_DESCRIPTION_MAX}
            </p>
          </div>

          {count === 0 && taskEditIndex !== 'new' ? (
            <div className="text-sm text-gray-400 py-2">Start by adding the first task.</div>
          ) : (
            <ol className="divide-y divide-gray-100" aria-label="Checklist tasks, in order" data-testid="checklist-task-list">
              {draft.tasks.map((t, i) => {
                const isEditingRow = taskEditIndex === i;
                return (
                  <li
                    key={i}
                    draggable={!editing}
                    onDragStart={(e) => { if (editing) return; setDragFrom(i); e.dataTransfer.effectAllowed = 'move'; try { e.dataTransfer.setData('text/plain', String(i)); } catch { /* some engines refuse */ } }}
                    onDragOver={(e) => { if (dragFrom === null) return; e.preventDefault(); if (dragOver !== i) setDragOver(i); }}
                    onDrop={(e) => { e.preventDefault(); if (dragFrom !== null) moveTask(dragFrom, i); setDragFrom(null); setDragOver(null); }}
                    onDragEnd={() => { setDragFrom(null); setDragOver(null); }}
                    className={`py-2 ${dragOver === i && dragFrom !== null && dragFrom !== i ? 'border-t-2 border-[#4AABDE]' : ''} ${dragFrom === i ? 'opacity-50' : ''}`}
                    data-testid={`checklist-task-${i}`}
                  >
                    {isEditingRow ? taskEditor : (
                      <div className="flex items-center gap-2">
                        <span aria-hidden="true" title={editing ? undefined : 'Drag to reorder'} className={`select-none text-gray-300 text-base leading-none ${editing ? '' : 'cursor-grab'}`}>⋮⋮</span>
                        <div className="min-w-0 flex-1 text-sm text-gray-500 truncate">
                          <span className="font-semibold text-gray-900">{t.taskText}</span>
                          <span> · {t.responsibleRole} · {describeDue(t.dueOffsetDays, draft.processKind)}</span>
                        </div>
                        <div className="flex items-center gap-1 flex-shrink-0">
                          <button
                            type="button" onClick={() => moveTask(i, i - 1)} disabled={editing || i === 0} aria-label={`Move “${t.taskText}” up`} data-testid={`checklist-up-${i}`}
                            className="rounded p-1 text-gray-500 hover:bg-gray-100 disabled:opacity-30 disabled:hover:bg-transparent"
                          ><svg width="14" height="14" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M5 12l5-5 5 5" /></svg></button>
                          <button
                            type="button" onClick={() => moveTask(i, i + 1)} disabled={editing || i === count - 1} aria-label={`Move “${t.taskText}” down`} data-testid={`checklist-down-${i}`}
                            className="rounded p-1 text-gray-500 hover:bg-gray-100 disabled:opacity-30 disabled:hover:bg-transparent"
                          ><svg width="14" height="14" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M5 8l5 5 5-5" /></svg></button>
                          <button type="button" onClick={() => startTaskEdit(i)} disabled={editing} aria-label={`Edit “${t.taskText}”`} className="text-xs text-[#4AABDE] hover:underline disabled:opacity-40 disabled:no-underline px-1" data-testid={`checklist-edit-${i}`}>Edit</button>
                        </div>
                      </div>
                    )}
                  </li>
                );
              })}
              {taskEditIndex === 'new' && <li className="py-2" data-testid="checklist-new-task">{taskEditor}</li>}
            </ol>
          )}
          {/* (Oct 20 visual pass) The first task of an EMPTY template is edited inside the list above (it renders whenever a new task is open). A second copy of the editor used to be
              rendered here too, so a brand-new template showed two identical task forms. */}

          {/* Add task is its own control, apart from the footer buttons. */}
          <div className="mt-3 pt-3 border-t border-gray-100">
            <button type="button" onClick={() => startTaskEdit('new')} disabled={editing} className="text-sm border border-gray-300 rounded px-4 py-1.5 text-gray-700 hover:bg-gray-50 disabled:opacity-40" data-testid="checklist-add-task">+ Add task</button>
          </div>
        </div>

        <div className="flex items-center gap-3 flex-wrap" data-testid="checklist-footer">
          <button type="button" onClick={handleSaveAndClose} disabled={saving || cannotManageTemplates} title={cannotManageTemplates ? templatesPermissionMessage : undefined} className="bg-[#4AABDE] hover:bg-[#3a94c8] text-white text-sm font-medium px-4 py-1.5 rounded disabled:opacity-50 disabled:cursor-not-allowed" data-testid="checklist-save-close">
            {saving ? 'Saving…' : 'Save and close'}
          </button>
          <button type="button" onClick={requestClose} disabled={saving} className="text-sm border border-gray-300 rounded px-4 py-1.5 text-gray-700 hover:bg-gray-50 disabled:opacity-50" data-testid="checklist-close">Close</button>
          {cannotManageTemplates && <PermissionHint label={PERMISSION_LABEL.checklistTemplates} what="change checklist templates" testId="checklist-needs-permission" />}
        </div>
        <p className="text-xs text-gray-400 mt-3">
          Saving updates the template for checklists assigned from now on. Existing employee checklists keep the version they were assigned.
        </p>
        {status && <div role="alert" data-testid="checklist-status" className="mt-2 text-sm text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2">{status}</div>}
        <div aria-live="polite" className="sr-only" data-testid="checklist-announce">{announce}</div>
      </div>
    );
  }

  // ── Template library (default view) ──────────────────────────────────────────────────────
  return (
    <div>
      {cameFrom?.returnTo && cameFrom.returnTo.startsWith('/hr/admin/employees/') && (
        <button type="button" className="text-sm text-[#176A9B] hover:underline mb-3" onClick={() => navigate(cameFrom.returnTo as string)} data-testid="back-to-employee">
          ← Back to {cameFrom.employeeName || 'the employee'}
        </button>
      )}
      <div className="flex items-start justify-between gap-3 mb-5">
        <div>
          <h1 className="text-xl font-bold text-gray-900 leading-tight">Checklist templates</h1>
          <p className="text-sm text-gray-500 mt-1">Build a reusable set of steps for joining or leaving the company.</p>
        </div>
        {/* Oct 20 Owner request: "Show inactive" sits in the header, just left of "+ New template". It appears only once at least one template is retired. */}
        <div className="flex items-center gap-3 flex-shrink-0">
          {templates !== null && templates.some((t) => t.retiredAt) && (
            <label className="inline-flex items-center gap-1.5 text-xs text-gray-600 cursor-pointer whitespace-nowrap">
              <input type="checkbox" className="h-3.5 w-3.5" checked={showRetired} onChange={(e) => setShowRetired(e.target.checked)} data-testid="checklist-show-retired" />
              Show inactive <span className="text-gray-400">({templates.filter((t) => t.retiredAt).length})</span>
            </label>
          )}
          <button type="button" onClick={startNew} disabled={cannotManageTemplates} title={cannotManageTemplates ? templatesPermissionMessage : undefined} data-testid="checklist-new-template"
            className="bg-[#4AABDE] hover:bg-[#3a94c8] text-white text-sm font-medium px-4 py-2 rounded-md flex-shrink-0 disabled:opacity-50 disabled:cursor-not-allowed">
            + New template
          </button>
        </div>
      </div>
      {cannotManageTemplates && <div className="mb-4 -mt-3"><PermissionHint label={PERMISSION_LABEL.checklistTemplates} what="create or change checklist templates" testId="checklist-library-needs-permission" /></div>}

      {notice && (
        <div role="status" data-testid="checklist-saved-notice" className="mb-4 flex items-start justify-between gap-3 rounded-md border border-green-200 bg-green-50 px-3 py-2 text-sm text-green-800">
          <span>{notice}</span>
          <button type="button" onClick={() => setNotice(null)} aria-label="Dismiss" className="text-green-800 underline text-xs flex-shrink-0">Dismiss</button>
        </div>
      )}

      {/* Oct 20 (Owner, visual pass): templates are shared by every administrator, so there is no "Your templates" heading. One compact row per template:
          large bold name, a kind badge (Onboarding / Offboarding) and the task count together. Onboarding templates list first, offboarding after
          (a stable sort, so the server's order is kept inside each group). */}
      {libraryError && (
        <div role="alert" data-testid="checklist-library-error" className="mb-4 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{libraryError}</div>
      )}

      <div className="bg-white rounded-lg border border-gray-200 shadow-sm px-4 py-2 mb-4">
        {templates === null && (templatesView.kind === 'denied' || templatesView.kind === 'unavailable') ? (
          <div className="py-2"><LoadViewNotice view={templatesView} thing="checklist templates" permission={PERMISSION_LABEL.checklistTemplates} onRetry={() => { setTemplatesView({ kind: 'loading' }); void refresh(); }} testIdPrefix="checklist-templates" /></div>
        ) : templates === null ? (
          <div className="text-sm text-gray-400 py-2">Loading…</div>
        ) : templates.length === 0 ? (
          <div className="text-sm text-gray-400 py-2">No templates yet.</div>
        ) : templates.filter((t) => showRetired || !t.retiredAt).length === 0 ? (
          <div className="text-sm text-gray-400 py-2" data-testid="checklist-no-active">No active templates. Tick “Show inactive” to see the retired ones.</div>
        ) : (
          <ul className="divide-y divide-gray-100" aria-label="Checklist templates" data-testid="checklist-template-list">
            {[...templates].filter((t) => showRetired || !t.retiredAt).sort((a, b) => (a.processKind === b.processKind ? 0 : a.processKind === 'onboarding' ? -1 : 1)).map((t) => (
              <li key={t.templateId} className="flex items-center justify-between gap-3 py-2.5" data-testid={`checklist-template-row-${t.templateId}`}>
                {/* One line on a normal screen: name, kind badge, task count, then the description, which takes the rest of the width and ends in "…" when it is longer
                    than the room (the full text is the tooltip). On a narrow screen the pieces wrap instead of being squeezed out. */}
                <div className="min-w-0 flex-1 flex items-center flex-wrap sm:flex-nowrap gap-x-3 gap-y-1">
                  <span className="text-xl font-bold text-gray-900 leading-tight truncate max-w-full sm:max-w-[45%] sm:flex-shrink-0" title={t.name}>{t.name}</span>
                  <span
                    className={`text-xs font-semibold uppercase tracking-wide rounded-full border px-2.5 py-0.5 flex-shrink-0 ${t.processKind === 'offboarding' ? 'bg-amber-100 text-amber-900 border-amber-300' : 'bg-green-100 text-green-800 border-green-300'}`}
                    data-testid={`checklist-template-kind-${t.templateId}`}
                  >
                    {t.processKind === 'offboarding' ? 'Offboarding' : 'Onboarding'}
                  </span>
                  <span className="text-sm text-gray-500 flex-shrink-0 whitespace-nowrap">{t.tasks.length} task{t.tasks.length === 1 ? '' : 's'}</span>
                  {t.shortDescription && (
                    <span className="text-sm text-gray-600 min-w-0 sm:flex-1 sm:truncate" title={t.shortDescription} data-testid={`checklist-template-description-${t.templateId}`}>({t.shortDescription})</span>
                  )}
                  {t.retiredAt && <span className="text-[11px] bg-gray-100 text-gray-500 rounded px-1.5 py-0.5 flex-shrink-0" data-testid={`checklist-template-retired-badge-${t.templateId}`}>Retired</span>}
                </div>
                {!t.retiredAt ? (
                  <div className="flex items-center gap-3 flex-shrink-0 text-xs">
                    <button type="button" onClick={() => startOpen(t)} className="text-[#4AABDE] hover:underline">Open template</button>
                    <button type="button" onClick={() => handleRetire(t)} disabled={cannotManageTemplates} title={cannotManageTemplates ? templatesPermissionMessage : undefined} className="text-red-600 hover:underline disabled:opacity-40 disabled:no-underline disabled:cursor-not-allowed" data-testid={`checklist-template-retire-${t.templateId}`}>Retire</button>
                  </div>
                ) : (
                  <div className="flex items-center gap-3 flex-shrink-0 text-xs">
                    <button type="button" onClick={() => startOpen(t)} className="text-[#4AABDE] hover:underline">View</button>
                    <button type="button" onClick={() => handleRestore(t)} disabled={cannotManageTemplates} title={cannotManageTemplates ? templatesPermissionMessage : undefined} className="text-[#4AABDE] hover:underline font-medium disabled:opacity-40 disabled:no-underline disabled:cursor-not-allowed" data-testid={`checklist-template-restore-${t.templateId}`}>Restore</button>
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-4">
          <h3 className="text-sm font-semibold text-gray-900">Assign to an employee</h3>
          <p className="text-xs text-gray-500 mt-1.5">Open the employee's HR record and choose their onboarding or offboarding checklist.</p>
          <button type="button" onClick={() => navigate('/hr/admin')} className="text-xs text-[#4AABDE] hover:underline mt-2.5 inline-block">Go to employees →</button>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-4">
          <h3 className="text-sm font-semibold text-gray-900">Need IT or another team?</h3>
          <p className="text-xs text-gray-500 mt-1.5">Add or rename responsibility labels in one place. These labels do not grant HR access.</p>
          <button type="button" onClick={() => navigate('/hr/admin/labels')} className="text-xs text-[#4AABDE] hover:underline mt-2.5 inline-block">Manage labels →</button>
        </div>
      </div>

      <details className="text-xs text-gray-500">
        <summary className="text-[#4AABDE] cursor-pointer">How this page works</summary>
        <p className="mt-2 max-w-lg">
          A template is the reusable master checklist. An assignment is an employee's copy. Editing the master does not rewrite tasks someone has already received or completed. Onboarding dates use the hire date; offboarding dates use the separation date.
        </p>
      </details>
    </div>
  );
}

// Owner-approved HR Administration layout (2026-10-07): "Responsibility labels: separate setup
// destination with existing add/rename/retire behavior." Moved out of ChecklistTemplatesPanel
// into its own route (/hr/admin/labels) per the decision — same createHrChecklistResponsibilityOption/
// renameHrChecklistResponsibilityOption/retireHrChecklistResponsibilityOption calls, unchanged.
// NEEDS DEV DEPLOY note (migration 085) carried over unchanged from the prior single-page version.
function ResponsibilityLabelsPage() {
  const [options, setOptions] = useState<HrChecklistResponsibilityOption[]>([]);
  const [newLabel, setNewLabel] = useState('');
  const [status, setStatus] = useState<string | null>(null);
  const [renamingId, setRenamingId] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState('');
  const [formOpen, setFormOpen] = useState(false);
  // Oct 24: "No options configured yet" is shown ONLY after a successful, authorized, empty read; a refused or failed read is shown as such.
  const [view, setView] = useState<LoadView>({ kind: 'loading' });
  const cannotManage = useSurfaceFlag('mayManageChecklistTemplates') === false;
  const permissionMessage = needsPermissionMessage(PERMISSION_LABEL.checklistTemplates, 'change responsibility labels');
  const refuse = (e: any, what: string, fallback: string) => actionErrorMessage(e, PERMISSION_LABEL.checklistTemplates, what, fallback);

  const refresh = () =>
    listHrChecklistResponsibilityOptions()
      .then((d) => { setOptions(d.options); setView({ kind: 'ready' }); })
      .catch((e) => setView((v) => (v.kind === 'ready' ? v : classifyLoadError(e, { gatedList: true }))));
  useEffect(() => { refresh(); }, []);

  async function handleAdd() {
    if (!newLabel.trim()) return;
    try {
      await createHrChecklistResponsibilityOption(newLabel.trim());
      setNewLabel('');
      setFormOpen(false);
      setStatus(null);
      refresh();
    } catch (e: any) {
      setStatus(refuse(e, 'add responsibility labels', 'Failed to add option.'));
    }
  }
  async function handleRename(optionId: string) {
    if (!renameValue.trim()) return;
    try {
      await renameHrChecklistResponsibilityOption(optionId, renameValue.trim());
      setRenamingId(null);
      setStatus(null);
      refresh();
    } catch (e: any) {
      setStatus(refuse(e, 'rename responsibility labels', 'Failed to rename option.'));
    }
  }
  async function handleRetire(optionId: string) {
    try {
      await retireHrChecklistResponsibilityOption(optionId);
      setStatus(null);
      refresh();
    } catch (e: any) {
      setStatus(refuse(e, 'retire responsibility labels', 'Failed to retire option.'));
    }
  }

  return (
    <div>
      <div className="flex items-start justify-between gap-3 mb-5">
        <div>
          <h1 className="text-xl font-bold text-gray-900 leading-tight">Responsibility labels</h1>
          <p className="text-sm text-gray-500 mt-1">Choose who normally handles each checklist task.</p>
        </div>
        <button type="button" onClick={() => setFormOpen((v) => !v)} disabled={cannotManage} title={cannotManage ? permissionMessage : undefined} data-testid="labels-add"
          className="bg-[#4AABDE] hover:bg-[#3a94c8] text-white text-sm font-medium px-4 py-2 rounded-md flex-shrink-0 disabled:opacity-50 disabled:cursor-not-allowed">
          + Add label
        </button>
      </div>
      {cannotManage && <div className="mb-4 -mt-3"><PermissionHint label={PERMISSION_LABEL.checklistTemplates} what="change responsibility labels" testId="labels-needs-permission" /></div>}

      <div className="bg-[#EDF5FC] rounded-md px-3 py-2.5 text-xs text-gray-700 mb-4">
        Labels such as IT and Accounting organize tasks. They do not grant permissions or automatically assign a specific person.
      </div>

      {formOpen && (
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-5 mb-4">
          <CardTitle>New responsibility label</CardTitle>
          <div className="flex gap-2">
            <input className="border border-gray-300 rounded px-3 py-1.5 text-sm flex-1" placeholder="e.g. IT support" value={newLabel} onChange={(e) => setNewLabel(e.target.value)} />
            <button onClick={handleAdd} className="bg-[#4AABDE] text-white text-sm px-4 py-1.5 rounded">Add in preview</button>
            <button type="button" onClick={() => setFormOpen(false)} className="text-sm text-gray-500 px-4 py-1.5">Cancel</button>
          </div>
          {status && <div className="text-sm text-red-700 mt-2">{status}</div>}
        </div>
      )}

      {status && !formOpen && <div role="alert" className="text-sm text-red-700 mb-3" data-testid="labels-status">{status}</div>}

      <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-5">
        <CardTitle>Available labels</CardTitle>
        {view.kind === 'loading' && <div className="text-sm text-gray-400">Loading…</div>}
        {(view.kind === 'denied' || view.kind === 'unavailable') && (
          <LoadViewNotice view={view} thing="responsibility labels" permission={PERMISSION_LABEL.checklistTemplates} onRetry={() => { setView({ kind: 'loading' }); void refresh(); }} testIdPrefix="labels" />
        )}
        {view.kind === 'ready' && (<>
        <ul className="divide-y divide-gray-100">
          {options.map((o) => (
            <li key={o.optionId} className="flex items-center justify-between gap-3 py-3 first:pt-0">
              {renamingId === o.optionId ? (
                <div className="flex gap-2 flex-1">
                  <input className="border border-gray-300 rounded px-2 py-1 text-sm flex-1" value={renameValue} onChange={(e) => setRenameValue(e.target.value)} />
                  <button onClick={() => handleRename(o.optionId)} className="text-[#4AABDE] text-xs">Save</button>
                  <button onClick={() => setRenamingId(null)} className="text-gray-500 text-xs">Cancel</button>
                </div>
              ) : (
                <>
                  <div>
                    <div className="text-sm font-medium text-gray-900">{o.label}</div>
                    <div className="text-xs text-gray-400 mt-0.5">Available for new checklist tasks</div>
                  </div>
                  <div className="flex gap-3 flex-shrink-0 text-xs">
                    <button onClick={() => { setRenamingId(o.optionId); setRenameValue(o.label); }} disabled={cannotManage} title={cannotManage ? permissionMessage : undefined} className="text-[#4AABDE] hover:underline disabled:opacity-40 disabled:no-underline disabled:cursor-not-allowed">Rename</button>
                    <button onClick={() => handleRetire(o.optionId)} disabled={cannotManage} title={cannotManage ? permissionMessage : undefined} className="text-red-600 hover:underline disabled:opacity-40 disabled:no-underline disabled:cursor-not-allowed">Retire</button>
                  </div>
                </>
              )}
            </li>
          ))}
          {options.length === 0 && <li className="py-3 text-sm text-gray-400">No options configured yet.</li>}
        </ul>
        </>)}
      </div>

      <details className="text-xs text-gray-500 mt-4">
        <summary className="text-[#4AABDE] cursor-pointer">How this page works</summary>
        <p className="mt-2 max-w-lg">Retiring a label removes it from future choices. Existing tasks retain their historical label.</p>
      </details>
    </div>
  );
}

// October 10 14:08 Phoenix addendum — "Document history reasons" admin-managed list, under HR
// setup's "Lists & labels" sub-heading alongside Responsibility labels (same page shape/
// conventions, deliberately mirrored). The four protected reasons (Replaced by newer completed
// document / Equipment returned / Duplicate record / Filed in error) cannot be retired-and-
// removed from the UI — they CAN be retired (deactivated) and reactivated, and their label/
// explanation can be edited, but their own typed requirement at Move-to-History time is keyed
// off a server-side, never-editable `code`, so a label edit can never remove it (see index.ts's
// handleMoveDocumentAssignmentToHistory). A newly admin-created reason is never protected and
// always requires the generic bounded supporting-detail field at use time — there is no
// "Other"/free-text reason anywhere in this feature.
function DocumentHistoryReasonsPage() {
  const [reasons, setReasons] = useState<HrDocumentHistoryReason[]>([]);
  const [newLabel, setNewLabel] = useState('');
  const [newExplanation, setNewExplanation] = useState('');
  const [status, setStatus] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editLabel, setEditLabel] = useState('');
  const [editExplanation, setEditExplanation] = useState('');
  const [formOpen, setFormOpen] = useState(false);
  const [retiredOpen, setRetiredOpen] = useState(false);

  // Oct 24: loading / ready / denied / unavailable are distinct (the reasons list is capability-gated; a refusal is never drawn as "No active reasons").
  const [view, setView] = useState<LoadView>({ kind: 'loading' });
  const cannotManage = useSurfaceFlag('mayManageDocuments') === false;
  const permissionMessage = needsPermissionMessage(PERMISSION_LABEL.documents, 'change document history reasons');
  const refuse = (e: any, what: string, fallback: string) => actionErrorMessage(e, PERMISSION_LABEL.documents, what, fallback);

  const refresh = () =>
    listHrDocumentHistoryReasons()
      .then((d) => { setReasons(d.reasons); setView({ kind: 'ready' }); })
      .catch((e) => setView((v) => (v.kind === 'ready' ? v : classifyLoadError(e, { gatedList: true }))));
  useEffect(() => { refresh(); }, []);

  async function handleAdd() {
    if (!newLabel.trim() || !newExplanation.trim()) return;
    try {
      await createHrDocumentHistoryReason({ label: newLabel.trim(), explanation: newExplanation.trim() });
      setNewLabel(''); setNewExplanation(''); setFormOpen(false); setStatus(null);
      refresh();
    } catch (e: any) {
      setStatus(refuse(e, 'add document history reasons', 'Failed to add reason.'));
    }
  }
  async function handleEdit(reasonId: string) {
    if (!editLabel.trim() || !editExplanation.trim()) return;
    try {
      await editHrDocumentHistoryReason(reasonId, { label: editLabel.trim(), explanation: editExplanation.trim() });
      setEditingId(null); setStatus(null);
      refresh();
    } catch (e: any) {
      setStatus(refuse(e, 'edit document history reasons', 'Failed to edit reason.'));
    }
  }
  async function handleRetire(reasonId: string) {
    try {
      await retireHrDocumentHistoryReason(reasonId);
      setStatus(null);
      refresh();
    } catch (e: any) {
      setStatus(refuse(e, 'deactivate document history reasons', 'Failed to deactivate reason.'));
    }
  }
  async function handleReactivate(reasonId: string) {
    try {
      await reactivateHrDocumentHistoryReason(reasonId);
      setStatus(null);
      refresh();
    } catch (e: any) {
      setStatus(refuse(e, 'reactivate document history reasons', 'Failed to reactivate reason.'));
    }
  }

  const active = reasons.filter((r) => !r.retiredAt);
  const retired = reasons.filter((r) => r.retiredAt);

  function renderReasonRow(r: HrDocumentHistoryReason, isRetired: boolean) {
    const editing = editingId === r.reasonId;
    return (
      <li key={r.reasonId} className="py-3 first:pt-0">
        {editing ? (
          <div className="flex flex-col gap-2">
            <input className="border border-gray-300 rounded px-2 py-1 text-sm" value={editLabel} onChange={(e) => setEditLabel(e.target.value)} placeholder="Label" />
            <input className="border border-gray-300 rounded px-2 py-1 text-sm" value={editExplanation} onChange={(e) => setEditExplanation(e.target.value)} placeholder="Explanation" />
            <div className="flex gap-2">
              <button onClick={() => handleEdit(r.reasonId)} className="text-[#4AABDE] text-xs">Save</button>
              <button onClick={() => setEditingId(null)} className="text-gray-500 text-xs">Cancel</button>
            </div>
          </div>
        ) : (
          <div className="flex items-start justify-between gap-3">
            <div>
              <div className="text-sm font-medium text-gray-900 flex items-center gap-2">
                {r.label}
                {r.isProtected && <span className="text-[10px] font-normal text-gray-400 bg-gray-100 rounded px-1.5 py-0.5">Built-in</span>}
              </div>
              <div className="text-xs text-gray-500 mt-0.5">{r.explanation}</div>
              {/* October 10 21:10 addendum: supporting detail is always optional for a
                  generic reason, old or new — never conditioned on this reason's own stored
                  requiresSupportingDetail flag (see MoveToHistoryModal.tsx for why that
                  per-reason flag can no longer be trusted as a requirement). */}
              {!r.isProtected && (
                <div className="text-xs text-gray-400 mt-0.5">Supporting note is optional when used</div>
              )}
            </div>
            <div className="flex gap-3 flex-shrink-0 text-xs">
              <button onClick={() => { setEditingId(r.reasonId); setEditLabel(r.label); setEditExplanation(r.explanation); }} disabled={cannotManage} title={cannotManage ? permissionMessage : undefined} className="text-[#4AABDE] hover:underline disabled:opacity-40 disabled:no-underline disabled:cursor-not-allowed">Edit</button>
              {isRetired ? (
                <button onClick={() => handleReactivate(r.reasonId)} disabled={cannotManage} title={cannotManage ? permissionMessage : undefined} className="text-green-700 hover:underline disabled:opacity-40 disabled:no-underline disabled:cursor-not-allowed">Reactivate</button>
              ) : (
                <button onClick={() => handleRetire(r.reasonId)} disabled={cannotManage} title={cannotManage ? permissionMessage : undefined} className="text-red-600 hover:underline disabled:opacity-40 disabled:no-underline disabled:cursor-not-allowed">Deactivate</button>
              )}
            </div>
          </div>
        )}
      </li>
    );
  }

  return (
    <div>
      <div className="flex items-start justify-between gap-3 mb-5">
        <div>
          <h1 className="text-xl font-bold text-gray-900 leading-tight">Document history reasons</h1>
          <p className="text-sm text-gray-500 mt-1">Choose the reasons HR may use to move a completed document to History.</p>
        </div>
        <button type="button" onClick={() => setFormOpen((v) => !v)} disabled={cannotManage} title={cannotManage ? permissionMessage : undefined} data-testid="reasons-add"
          className="bg-[#4AABDE] hover:bg-[#3a94c8] text-white text-sm font-medium px-4 py-2 rounded-md flex-shrink-0 disabled:opacity-50 disabled:cursor-not-allowed">
          + Add reason
        </button>
      </div>
      {cannotManage && <div className="mb-4 -mt-3"><PermissionHint label={PERMISSION_LABEL.documents} what="change document history reasons" testId="reasons-needs-permission" /></div>}

      <div className="bg-[#EDF5FC] rounded-md px-3 py-2.5 text-xs text-gray-700 mb-4">
        A reason only moves a record's placement to History — it never automatically returns equipment, supersedes another record, changes review completion, satisfies a checklist, or invalidates a signature. The four built-in reasons keep their own required details even if their label is edited.
      </div>

      {formOpen && (
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-5 mb-4">
          <CardTitle>New history reason</CardTitle>
          <div className="flex flex-col gap-2">
            <input className="border border-gray-300 rounded px-3 py-1.5 text-sm" placeholder="Label (e.g. Retention period expired)" value={newLabel} onChange={(e) => setNewLabel(e.target.value)} />
            <input className="border border-gray-300 rounded px-3 py-1.5 text-sm" placeholder="Explanation shown to HR when choosing this reason" value={newExplanation} onChange={(e) => setNewExplanation(e.target.value)} />
            <div className="flex gap-2">
              <button onClick={handleAdd} className="bg-[#4AABDE] text-white text-sm px-4 py-1.5 rounded">Add reason</button>
              <button type="button" onClick={() => setFormOpen(false)} className="text-sm text-gray-500 px-4 py-1.5">Cancel</button>
            </div>
          </div>
          {status && <div className="text-sm text-red-700 mt-2">{status}</div>}
        </div>
      )}

      {status && !formOpen && <div role="alert" className="text-sm text-red-700 mb-3" data-testid="reasons-status">{status}</div>}

      <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-5">
        <CardTitle>Active reasons</CardTitle>
        {view.kind === 'loading' && <div className="text-sm text-gray-400">Loading…</div>}
        {(view.kind === 'denied' || view.kind === 'unavailable') && (
          <LoadViewNotice view={view} thing="document history reasons" permission={PERMISSION_LABEL.documents} onRetry={() => { setView({ kind: 'loading' }); void refresh(); }} testIdPrefix="reasons" />
        )}
        {view.kind === 'ready' && (
        <ul className="divide-y divide-gray-100">
          {active.map((r) => renderReasonRow(r, false))}
          {active.length === 0 && <li className="py-3 text-sm text-gray-400">No active reasons.</li>}
        </ul>
        )}
        {view.kind === 'ready' && retired.length > 0 && (
          <div className="mt-4 border-t border-gray-100 pt-2">
            <button type="button" onClick={() => setRetiredOpen((v) => !v)} className="text-xs font-semibold text-gray-500 uppercase tracking-wide hover:text-gray-700">
              Deactivated ({retired.length}) {retiredOpen ? '▾' : '▸'}
            </button>
            {retiredOpen && (
              <ul className="divide-y divide-gray-100 mt-2">
                {retired.map((r) => renderReasonRow(r, true))}
              </ul>
            )}
          </div>
        )}
      </div>

      <details className="text-xs text-gray-500 mt-4">
        <summary className="text-[#4AABDE] cursor-pointer">How this page works</summary>
        <div className="mt-2 max-w-lg space-y-1.5">
          <p>Deactivating a reason removes it from the choices offered when moving a document to History going forward — it is never deleted, and any past History entry that used it keeps its own saved wording exactly as it was at the time, even if you later edit or reactivate the reason.</p>
          <p>Editing a label or explanation never changes what a reason already means — the four built-in reasons keep their own specific required information (like confirming a whole equipment return) no matter how they're labeled.</p>
        </div>
      </details>
    </div>
  );
}
